import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import showNotification from '../../utilities/showNotification';
import CustomAlert from '../../components/customs/customAlert';
import LabGradingRubric from '../labEngine/labGradingRubric';
import SimulationReport from '../labEngine/simulationReport';
import { formatShortDate, formatDateTime } from '../../utilities/dateUtils';
import jsPDF from 'jspdf';
import {
  useCourse, 
  useInstructor, 
  useBlobStorage, 
  useSimulator, 
  useStudent, 
  useVisualCircuit
} from '../../hooks';


import './AssessmentModal.css';

// ─── Type config ─────────────────────────────────────────────────────────────
const TYPE_CFG = {
  lab:  { icon: '🧪', label: 'Lab',  idField: 'labId'  },
  quiz: { icon: '📋', label: 'Quiz', idField: 'quizId' },
  exam: { icon: '📄', label: 'Exam', idField: 'examId' },
  capstone: { icon: '📄', label: 'Capstone', idField: 'capstoneId' },
};

const formatType = (t = '') => t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ─── Component ────────────────────────────────────────────────────────────────
const AssessmentModal = ({
  visible,
  authData,
  courseId,
  domain,
  role = 'instructor',
  phase = 'overview',
  assessmentType = 'lab',
  content,
  onLoad,
  onClose,
  onDownload,
  onUploadFiles,
  onRemoveNewFile,
  // Grading phase props
  submission,
  gradeFormData,
  onGradeFormChange,
  labSessions,
  labGradingModals,
  setLabGradingModals,
  onSubmitGrade,
  studentName,
  studentLoginId,
  courseProgress,
  courseCircuits,
  allStudentCircuits,
  onAfterSubmit,
}) => {

  // Derive selectedContentType internally from assessmentType
  const selectedContentType = assessmentType === 'lab'  ? 'labs'
                            : assessmentType === 'quiz' ? 'quizzes'
                            : assessmentType === 'exam' ? 'exams'
                            : assessmentType === 'capstone' ? 'capstones'
                            : null;
  

  // ── HOOKS ──────────────────────────────────────────────────────────────────
  const { updateCourseContent, getCourseById } = useCourse();
  const { generateQuestionRubric, generateCompleteLabRequirements } = useInstructor();
  const { uploadFile } = useBlobStorage();
  const { getSimulatorBySessionId } = useSimulator();
  const { submitLabAnswers, submitQuizAnswers, submitExamAnswers, 
    getLabProgress, getQuizProgress, getExamProgress } = useStudent();
  const { getCircuitBySessionId } = useVisualCircuit();


  // ── References ──────────────────────────────────────────────────────────────────
  const modalBodyRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const timerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const editedContentRef = useRef(null);
  // ── States ──────────────────────────────────────────────────────────────────
  const [editing,                    setEditing]                    = useState(false);
  const [editedContent,              setEditedContent]              = useState(null);
  const [expandedSections,           setExpandedSections]           = useState({ labInfo: false, experimental: false, postLab: false, questions: false });
  const [expandedRubrics,            setExpandedRubrics]            = useState({});
  const [editedContentFiles,         setEditedContentFiles]         = useState({});
  const [uploadingContentFiles,      setUploadingContentFiles]      = useState(false);
  const [contentFileUploadProgress,  setContentFileUploadProgress]  = useState({});

  // Rubric states
  const [generatingRubric,           setGeneratingRubric]           = useState(false);
  const [rubricPreview,              setRubricPreview]              = useState(null);
  const [editableRubric,             setEditableRubric]             = useState(null);
  const [isEditingRubric,            setIsEditingRubric]            = useState(false);
  const [rubricQuestionIndex,        setRubricQuestionIndex]        = useState(null);

  // Practical Lab requirements states
  const [isGeneratingRequirements,   setIsGeneratingRequirements]   = useState(false);
  const [generatedPreview,           setGeneratedPreview]           = useState(null);
  const [generationRequirementError, setGenerationRequirementError] = useState(null);
  const [editingLabRequirements,     setEditingLabRequirements]     = useState({});

  const [answers, setAnswers]             = useState({});
  const [essayFiles, setEssayFiles]       = useState({});
  const [isSubmitting, setIsSubmitting]   = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submissionResults, setSubmissionResults] = useState(null);
  const [assessmentPhase, setAssessmentPhase] = useState(phase);
  const [isGrading, setIsGrading] = useState(false);
  
  const [submitConfirmAlert, setSubmitConfirmAlert] = useState(false);
  const [submitConfirmMessage, setSubmitConfirmMessage] = useState('');
  const [validationAlert, setValidationAlert] = useState(false);
  const [validationAlertMessage, setValidationAlertMessage] = useState('');

  const [timeRemaining, setTimeRemaining] = useState(null);
    

  useEffect(() => {
    setAssessmentPhase(phase);
    setAnswers({});
    setEssayFiles({});
    setSubmissionResults(null);
    setIsSubmitting(false);
  }, [phase, content]);

  useEffect(() => {
    setAnswers({});
    setEssayFiles({});
    setSubmissionResults(null);
    setIsSubmitting(false);
    setUploadingFiles(false);
    setUploadProgress({});
  }, [content]);

  useEffect(() => {
    if (content) {
      const fresh = [JSON.parse(JSON.stringify(content))];
      setEditedContent(fresh);
      editedContentRef.current = fresh;
    }
  }, [content]);

  useEffect(() => {
    if (!visible) {
      setEditing(false);
      setEditedContent(null);
      setExpandedRubrics({});
      setEditedContentFiles({});
      setContentFileUploadProgress({});
      setGeneratingRubric(false);
      setRubricPreview(null);
      setEditableRubric(null);
      setIsEditingRubric(false);
      setRubricQuestionIndex(null);
      setGeneratedPreview(null);
      setGenerationRequirementError(null);
      setEditingLabRequirements({});
    }
  }, [visible]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Start timer when entering attempt phase for quiz/exam
  useEffect(() => {
    console.log('⏱ Timer useEffect:', { assessmentPhase, assessmentType, duration: content?.duration });
    if (assessmentPhase === 'attempt' &&
        (assessmentType === 'quiz' || assessmentType === 'exam') && 
        content?.duration) {
      
      const totalSeconds = content.duration * 60;
      setTimeRemaining(totalSeconds);

      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            autoSubmitRef.current = true;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [assessmentPhase, assessmentType, content?.duration]);

  useEffect(() => {
    if (timeRemaining === 0 && assessmentPhase === 'attempt') {
      showNotification('⏰ Time is up! Submitting automatically...', 'warning');
      handleSubmitAttempt();
    }
  }, [timeRemaining, assessmentPhase]);

  if (!visible || !content) return null;

  const typeCfg     = TYPE_CFG[assessmentType] || TYPE_CFG.lab;
  const idx         = 0; // modal always operates on a single item
  const displayItem = editing ? (editedContent?.[0] || content) : content;

  const uploadEssayAttachments = async (assessmentType, assessmentId) => {
    const uploadedFiles = {};
    const questionIds = Object.keys(essayFiles);
    if (questionIds.length === 0) return uploadedFiles;

    setUploadingFiles(true);
    try {
      for (const questionId of questionIds) {
        const files = essayFiles[questionId];
        if (!files || files.length === 0) continue;

        uploadedFiles[questionId] = { fileIds: [], fileNames: [] };

        let attemptNumber = 1;
        if (assessmentType === 'quiz') {
          const p = courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === assessmentId);
          attemptNumber = (p?.attemptsUsed || 0) + 1;
        } else if (assessmentType === 'exam') {
          const p = courseProgress?.examProgress?.exams?.find(e => e.examId === assessmentId);
          attemptNumber = (p?.attemptsUsed || 0) + 1;
        }

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.size > 5 * 1024 * 1024) {
            showNotification(`File ${file.name} exceeds 5MB limit`, 'error');
            continue;
          }
          const allowedTypes = ['application/pdf', 'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
          if (!allowedTypes.includes(file.type)) {
            showNotification(`File ${file.name} has invalid type`, 'error');
            continue;
          }
          setUploadProgress(prev => ({ ...prev, [questionId]: `Uploading ${i + 1}/${files.length}...` }));
          const fileExtension = file.name.split('.').pop();
          const structuredFilename = `${authData.id}/${courseId}/${assessmentType}/${assessmentId}/${questionId}/attempt${attemptNumber}_file${i + 1}.${fileExtension}`;
          const metadata = {
            studentId: authData.id, courseId, assessmentType, assessmentId,
            questionId, attemptNumber, uploadedBy: authData.id,
            originalFilename: file.name, structuredFilename
          };
          const uploadResult = await uploadFile(file, metadata);
          if (uploadResult.success) {
            uploadedFiles[questionId].fileIds.push(uploadResult.data.fileId);
            uploadedFiles[questionId].fileNames.push(file.name);
          } else {
            showNotification(`Failed to upload ${file.name}`, 'error');
          }
        }
        setUploadProgress(prev => ({ ...prev, [questionId]: 'Complete' }));
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading essay attachments:', error);
      showNotification('Failed to upload some files', 'error');
      return uploadedFiles;
    } finally {
      setUploadingFiles(false);
      setUploadProgress({});
    }
  };

  const generatePDF = async () => {
    try {
      showNotification('Generating PDF...', 'info');
      const { jsPDF } = await import('jspdf');

      const getBase64FromUrl = async (url) => {
        try {
          const res  = await fetch(url);
          const blob = await res.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch { return null; }
      };

      const logoBase64 = await getBase64FromUrl('/images/Prof_iX.png');
      const pdf        = new jsPDF('p', 'mm', 'a4');
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin     = 15;
      const maxWidth   = pageWidth - margin * 2;
      let y            = margin;

      // ── Colours ──
      const C = {
        blue:       [0,   102, 204],
        blueDark:   [0,   70,  150],
        teal:       [0,   128, 128],
        tealLight:  [230, 245, 245],
        amber:      [180, 100, 0],
        amberLight: [255, 245, 225],
        green:      [0,   130, 80],
        greenLight: [230, 248, 240],
        red:        [180, 30,  30],
        gray:       [100, 100, 100],
        grayLight:  [245, 245, 245],
        dark:       [20,  20,  20],
        mid:        [60,  60,  60],
        light:      [140, 140, 140],
        white:      [255, 255, 255],
        border:     [210, 210, 210],
        headerBg:   [0,   70,  150],
      };

      const sanitize = (text) => String(text || '')
        .replace(/[•·]/g, '-')
        .replace(/['']/g, "'")
        .replace(/[""]/g, '"')
        .replace(/[–—]/g, '-')
        .replace(/[→=>]/g, '->')
        .replace(/`/g, "'")
        .replace(/[^\x20-\x7E\n]/g, '')
        .replace(/[ \t]+/g, ' ')
        .trim();

      const checkPage = (needed = 10) => {
        if (y + needed > pageHeight - 20) {
          pdf.addPage();
          y = margin;
        }
      };

      // ── Draw filled rectangle ──
      const fillRect = (x, ry, w, h, color) => {
        pdf.setFillColor(...color);
        pdf.rect(x, ry, w, h, 'F');
      };

      // ── Draw border rectangle ──
      const strokeRect = (x, ry, w, h, color, lineWidth = 0.3) => {
        pdf.setDrawColor(...color);
        pdf.setLineWidth(lineWidth);
        pdf.rect(x, ry, w, h, 'S');
      };

      // ── Add text with wrap ──
      const addText = (text, fontSize = 10, color = C.dark, bold = false, x = margin, maxW = maxWidth) => {
        pdf.setFontSize(fontSize);
        pdf.setTextColor(...color);
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        const clean = sanitize(text);
        const lines = pdf.splitTextToSize(clean, maxW);
        const lineH = fontSize * 0.42 + 1.5;
        checkPage(lines.length * lineH + 2);
        pdf.text(lines, x, y);
        y += lines.length * lineH;
        return lines.length * lineH;
      };

      // ── Section header with colored background ──
      const addSectionHeader = (title, bgColor = C.headerBg, textColor = C.white) => {
        checkPage(14);
        fillRect(margin, y, maxWidth, 10, bgColor);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...textColor);
        pdf.text(sanitize(title), margin + 4, y + 7);
        y += 13;
      };

      // ── Info row (label: value) ──
      const addInfoRow = (label, value) => {
        checkPage(7);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...C.gray);
        pdf.text(sanitize(label) + ':', margin + 2, y);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...C.dark);
        pdf.text(sanitize(String(value || '-')), margin + 40, y);
        y += 6;
      };

      // ── Info grid (2-column) ──
      const addInfoGrid = (items) => {
        const colW = maxWidth / 2;
        let col    = 0;
        let rowY   = y;
        items.forEach(([label, value]) => {
          if (!value) return;
          const x = margin + col * colW;
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...C.gray);
          pdf.text(sanitize(label) + ':', x + 2, rowY);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...C.dark);
          pdf.text(sanitize(String(value)), x + 28, rowY);
          col++;
          if (col === 2) { col = 0; rowY += 6; }
        });
        y = rowY + (col > 0 ? 6 : 0) + 2;
      };

      // ── Divider ──
      const addDivider = (color = C.border) => {
        checkPage(5);
        pdf.setDrawColor(...color);
        pdf.setLineWidth(0.3);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 4;
      };

      // ── Question box ──
      const addQuestionBox = (q, idx, accentColor, labelColor, labelText) => {
        const qText   = sanitize(q.questionText || '');
        const lines   = pdf.splitTextToSize(qText, maxWidth - 14);
        const lineH   = 9 * 0.42 + 1.5;
        let boxH      = 8 + lines.length * lineH + 4;

        // options
        if (q.questionType === 'multiple_choice' && q.options?.length) {
          boxH += q.options.length * 6 + 4;
        }
        
        // correct answer
        if (q.correctAnswer && q.questionType !== 'multiple_choice') {
          const aFontSize = q.questionType === 'code' ? 7.5 : 8;
          const aLines    = pdf.splitTextToSize(sanitize(q.correctAnswer), maxWidth - 24);
          const aLineH    = aFontSize * 0.42 + 1.5;
          boxH += 10 + aLines.length * aLineH;
        } else if (q.correctAnswer) {
          boxH += 12;
        }

        checkPage(boxH + 6);

        // Box background
        fillRect(margin, y, maxWidth, boxH, C.grayLight);
        // Left accent bar
        fillRect(margin, y, 3, boxH, accentColor);
        // Border
        strokeRect(margin, y, maxWidth, boxH, C.border, 0.2);

        const innerX = margin + 6;
        const innerW = maxWidth - 10;
        let qy       = y + 6;

        // Question header: Q# · Type · pts
        pdf.setFontSize(8.5);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...labelColor);
        pdf.text(`Q${idx + 1}`, innerX, qy);

        // Type badge
        const typeLabel = labelText || formatType(q.questionType);
        const typeX     = innerX + 10;
        const badgeW    = pdf.getTextWidth(typeLabel) + 4;
        fillRect(typeX, qy - 4, badgeW, 5.5, accentColor);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...C.white);
        pdf.text(typeLabel, typeX + 2, qy);

        // Points
        const ptsText = `${q.points} pts`;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...C.gray);
        pdf.text(ptsText, pageWidth - margin - pdf.getTextWidth(ptsText) - 2, qy);

        qy += 5;

        // Question text
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...C.dark);
        pdf.text(lines, innerX, qy);
        qy += lines.length * lineH + 2;

        // MCQ options
        if (q.questionType === 'multiple_choice' && q.options?.length) {
          q.options.forEach((opt, oIdx) => {
            const letter    = String.fromCharCode(65 + oIdx);
            const isCorrect = opt === q.correctAnswer;
            if (isCorrect) {
              fillRect(innerX, qy - 4, innerW - 4, 6.5, C.greenLight);
            }
            pdf.setFontSize(8.5);
            pdf.setFont('helvetica', isCorrect ? 'bold' : 'normal');
            pdf.setTextColor(...(isCorrect ? C.green : C.mid));
            pdf.text(`${letter}.  ${sanitize(opt)}${isCorrect ? '  [Correct]' : ''}`, innerX + 2, qy);
            qy += 5.5;
          });
          qy += 1;
        }

        // Correct answer box
        if (q.correctAnswer && q.questionType !== 'multiple_choice') {
          const isCode    = q.questionType === 'code';
          const aFont     = isCode ? 'courier' : 'helvetica';
          const aFontSize = isCode ? 7.5 : 8;
          const aMaxW     = innerW - 8;
          pdf.setFontSize(aFontSize);
          pdf.setFont(aFont, 'normal');
          const aLines    = pdf.splitTextToSize(sanitize(q.correctAnswer), aMaxW);
          const aLineH    = aFontSize * 0.42 + 1.5;
          const aBoxH     = 8 + aLines.length * aLineH + 2;

          fillRect(innerX, qy - 3, innerW - 4, aBoxH, C.greenLight);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...C.green);
          pdf.text('Correct Answer:', innerX + 2, qy + 1);
          pdf.setFont(aFont, 'normal');
          pdf.setTextColor(...C.dark);
          pdf.text(aLines, innerX + 2, qy + 7);
          qy += aBoxH;
        }

        y += boxH + 4;
      };

      // ════════════════════════════════════════════════
      // HEADER
      // ════════════════════════════════════════════════
      // Blue header bar
      fillRect(0, 0, pageWidth, 38, C.headerBg);
      if (logoBase64) {
        pdf.addImage(logoBase64, 'PNG', margin, 5, 24, 24);
      }
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...C.white);
      pdf.text('Prof iX', margin + 28, 17);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(180, 210, 255);
      pdf.text('Intelligent Learning Platform', margin + 28, 24);

      // Title on right side of header
      const titleText  = sanitize(content?.title || typeCfg.label);
      const titleLines = pdf.splitTextToSize(titleText, 90);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...C.white);
      titleLines.forEach((line, i) => {
        pdf.text(line, pageWidth - margin - pdf.getTextWidth(line), 14 + i * 7);
      });

      // ID badge under title
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(180, 210, 255);
      pdf.text(`${typeCfg.label}  ${content?.[typeCfg.idField] || ''}`, pageWidth - margin - 60, 28);

      y = 46;

      // ════════════════════════════════════════════════
      // INFO SECTION
      // ════════════════════════════════════════════════
      addSectionHeader(`${typeCfg.label} Info`, C.blueDark);

      const infoItems = [
        ['Due Date',         content?.dueDate    ? formatShortDate(content.dueDate) : null],
        ['Duration',         content?.duration   ? content.duration + ' min'        : null],
        ['Grade Weight',     content?.gradeWeight],
        ['Max Points',       content?.maxGrade],
        ['Attempts Allowed', content?.attemptsAllowed],
      ].filter(([, v]) => v != null);
      addInfoGrid(infoItems);

      if (content?.description) {
        checkPage(10);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...C.gray);
        pdf.text('Description', margin + 2, y);
        y += 5;
        addText(content.description, 9, C.mid, false, margin + 2, maxWidth - 4);
        y += 2;
      }

      if (content?.instructions) {
        checkPage(10);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...C.gray);
        pdf.text('Instructions', margin + 2, y);
        y += 5;
        addText(content.instructions, 9, C.mid, false, margin + 2, maxWidth - 4);
        y += 2;
      }

      if (content?.resources) {
        checkPage(10);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...C.gray);
        pdf.text('Resources', margin + 2, y);
        y += 5;
        addText(content.resources, 9, C.mid, false, margin + 2, maxWidth - 4);
        y += 2;
      }

      y += 2;

      // ════════════════════════════════════════════════
      // QUESTIONS
      // ════════════════════════════════════════════════
      const allQuestions = content?.questions || [];
      const practicalQs        = assessmentType === 'lab' ? allQuestions.filter(q => q.questionType === 'lab_practical') : [];
      const nonPracticalQs     = assessmentType === 'lab' ? allQuestions.filter(q => q.questionType !== 'lab_practical') : allQuestions;

      if (assessmentType === 'lab' && practicalQs.length > 0) {
        addSectionHeader(`Experimental Questions (${practicalQs.length})`, C.teal);
        practicalQs.forEach((q, idx) => addQuestionBox(q, idx, C.teal, C.teal, 'Lab Practical'));
      }

      if (nonPracticalQs.length > 0) {
        const secTitle = assessmentType === 'lab'
          ? `Post-Lab Questions (${nonPracticalQs.length})`
          : `Questions (${nonPracticalQs.length})`;
        addSectionHeader(secTitle, C.amber);
        nonPracticalQs.forEach((q, idx) => {
          const typeLabel = formatType(q.questionType);
          addQuestionBox(q, idx, C.amber, C.amber, typeLabel);
        });
      }

      // ════════════════════════════════════════════════
      // FOOTER on every page
      // ════════════════════════════════════════════════
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(...C.border);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);

        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...C.light);

        // Left: title + page
        pdf.text(
          sanitize(`${content?.title || typeCfg.label}  Page ${i} of ${totalPages}`),
          margin, pageHeight - 8
        );

        // Center: copyright
        const copy      = 'Copyright FutureiX Inc. | info@futureix.ca';
        const copyW     = pdf.getTextWidth(copy);
        pdf.text(copy, (pageWidth - copyW) / 2, pageHeight - 8);

        // Right: date
        const dateStr = new Date().toLocaleDateString();
        pdf.text(dateStr, pageWidth - margin - pdf.getTextWidth(dateStr), pageHeight - 8);
      }

      pdf.save(`${sanitize(content?.title || assessmentType)}_overview.pdf`);
      showNotification('PDF downloaded!', 'success');
    } catch (error) {
      console.error('PDF generation error:', error);
      showNotification('Failed to generate PDF', 'error');
    }
  };
  
  const handleSubmitAttempt = async (autoSubmit = false) => {
    const isAutoSubmit = autoSubmit || autoSubmitRef.current;
    autoSubmitRef.current = false;
    
    if (!content) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const unanswered = content.questions?.filter(q => 
      q.questionType !== 'lab_practical' && !answers[q.questionId]
    ) || [];
    
    if (unanswered.length > 0 && !isAutoSubmit) {
      setSubmitConfirmMessage(`You have ${unanswered.length} unanswered question(s). Submit anyway?`);
      setSubmitConfirmAlert(true);
      return;
    }
    if (unanswered.length > 0 && isAutoSubmit) {
      showNotification(`You have ${unanswered.length} unanswered question(s). Submitted anyway!`, 'warning');
    }
    handleConfirmedSubmitAttempt();
  };

  const handleConfirmedSubmitAttempt = async () => {
    setIsSubmitting(true);
    try {
      if (assessmentType === 'lab') {
        const uploadedFileIds = await uploadEssayAttachments('lab', content.labId);

        // ── Build answer list for ALL questions ──
        const answerList = await Promise.all(content.questions.map(async (question) => {
          const questionId = question.questionId;
          const answerData = answers[questionId];

          const answerObj = {
            questionId,
            questionType: question.questionType || 'unknown',
            answer: typeof answerData === 'object' ? (answerData.answer || '') : (answerData || '')
          };

          // ── Lab Practical: fetch grading data from simulator ──
          if (question.questionType === 'lab_practical') {
            // Find sessionId from answers state or courseCircuits
            const sessionId = (typeof answerData === 'object' && answerData.sessionId)
              ? answerData.sessionId
              : courseCircuits?.find(c =>
                  c.courseId === courseId &&
                  c.labId === content.labId &&
                  c.questionId === questionId &&
                  c.sessionId
                )?.sessionId || null;

            if (sessionId) {
              answerObj.sessionId = sessionId;
              // Fetch simulator to get grading payload
              try {
                const simRes = await getSimulatorBySessionId(sessionId);
                if (simRes?.success && simRes.data?.gradingPayload) {
                  const gp = simRes.data.gradingPayload;
                  answerObj.points            = gp.gradingResult?.totalScore ?? 0;
                  answerObj.maxPoints         = question.points || 0;
                  answerObj.aiGradeMetadata   = gp.gradingResult || null;
                  const simDomain = simRes?.data?.domain || 'IoT';
                  answerObj.answer = simDomain === 'AI'
                    ? `AI Notebook Session: ${sessionId}`
                    : simDomain === 'NodeRed'
                    ? `NodeRed Flow Session: ${sessionId}`
                    : `IoT Session: ${sessionId}`;
                }
              } catch (err) {
                console.error(`❌ Failed to fetch simulator for Q${questionId}:`, err);
              }
            } else {
              console.warn(`⚠️ Lab Q${questionId}: No sessionId found`);
              answerObj.points    = 0;
              answerObj.maxPoints = question.points || 0;
            }
          }

          // ── Unanswered non-practical questions ──
          if (question.questionType !== 'lab_practical' && !answerData) {
            answerObj.points    = 0;
            answerObj.maxPoints = question.points || 0;
          }

          if (uploadedFileIds[questionId]?.fileIds?.length > 0) {
            answerObj.attachmentFileIds   = uploadedFileIds[questionId].fileIds;
            answerObj.attachmentFileNames = uploadedFileIds[questionId].fileNames;
          }

          return answerObj;
        }));

        const response = await submitLabAnswers(authData.id, {
          courseId, labId: content.labId, answers: answerList, attachments: []
        });

        if (!response.success) {
          showNotification(response.message || 'Submission failed', 'error');
          return;
        }

        const results = {
          type: 'lab',
          title: content.title,
          labSessions: response.data?.labSessions || {},
          questions: content.questions.map(q => {
            const answerData    = answers[q.questionId];
            const studentAnswer = typeof answerData === 'object'
              ? (answerData.answer || 'No answer')
              : (answerData || 'No answer');
            const needsGrading  = ['essay', 'code', 'short_answer', 'lab_practical'].includes(q.questionType);
            const isCorrect     = needsGrading
              ? null
              : studentAnswer.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase();
            const earnedPoints  = isCorrect ? q.points : 0;
            return {
              questionId:    q.questionId,
              questionText:  q.questionText,
              questionType:  q.questionType,
              options:       q.options || [],
              studentAnswer,
              correctAnswer: q.correctAnswer || null,
              isCorrect,
              points:        q.points || 0,
              earnedPoints,
              explanation:   q.explanation || null,
              needsGrading,
              metadata: {
                sessionId: typeof answerData === 'object' ? answerData.sessionId : null
              }
            };
          }),
          totalPoints:  content.questions.reduce((sum, q) => sum + (q.points || 0), 0),
          earnedPoints: content.questions.reduce((sum, q) => {
            if (['essay', 'code', 'lab_practical'].includes(q.questionType)) return sum;
            const ad = answers[q.questionId];
            const sa = typeof ad === 'object' ? (ad.answer || '') : (ad || '');
            return sum + (sa.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase() ? (q.points || 0) : 0);
          }, 0),
          hasManualGrading: content.questions.some(q =>
            ['essay', 'code', 'short_answer', 'lab_practical'].includes(q.questionType)
          )
        };

        setSubmissionResults(results);

        setAssessmentPhase('results');
        setIsGrading(true);

        let pollCount = 0;
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(async () => {
          pollCount++;
          try {
            const progressRes = await getLabProgress(authData.id, courseId);
            if (progressRes.success) {
              const lab = progressRes.data?.labs?.find(l => l.labId === content.labId);
              const lastAttempt = lab?.attempts?.[lab.attempts.length - 1];
              const allGraded = lastAttempt?.answers?.every(a =>
                a.questionType === 'multiple_choice' ||
                a.questionType === 'true_false' ||
                a.aiGradeMetadata != null
              );

              if (allGraded || pollCount >= 15) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                setIsGrading(false);
                onAfterSubmit?.();
              }
            }
          } catch (err) {
            console.error('Polling error:', err);
            if (pollCount >= 15) {
              clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              setIsGrading(false);
              onAfterSubmit?.();
            }
          }
        }, 2000);

      } else if (assessmentType === 'quiz') {
        const uploadedFileIds = await uploadEssayAttachments('quiz', content.quizId);
        const answerList = content.questions.map(question => {
          const questionId = question.questionId;
          const answer     = answers[questionId] || '';
          const answerObj  = { questionId, questionType: question.questionType || 'unknown', answer };
          
          if (uploadedFileIds[questionId]?.fileIds?.length > 0) {
            answerObj.attachmentFileIds   = uploadedFileIds[questionId].fileIds;
            answerObj.attachmentFileNames = uploadedFileIds[questionId].fileNames;
          }
          return answerObj;
        });

        const response = await submitQuizAnswers(authData.id, {
          courseId, quizId: content.quizId, answers: answerList
        });
        if (!response.success) { showNotification(response.message || 'Submission failed', 'error'); return; }

        const results = {
          type: 'quiz', title: content.title,
          questions: content.questions.map(q => {
            const studentAnswer = answers[q.questionId] || 'No answer';
            const isCorrect     = studentAnswer.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase();
            return {
              questionId: q.questionId, questionText: q.questionText, questionType: q.questionType,
              options: q.options || [], studentAnswer, correctAnswer: q.correctAnswer || null,
              isCorrect, points: q.points || 0, earnedPoints: isCorrect ? (q.points || 0) : 0,
              explanation: q.explanation || null, needsGrading: false
            };
          }),
          totalPoints:  content.questions.reduce((sum, q) => sum + (q.points || 0), 0),
          earnedPoints: content.questions.reduce((sum, q) => {
            const sa = answers[q.questionId] || '';
            return sum + (sa.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase() ? (q.points || 0) : 0);
          }, 0),
          hasManualGrading: false
        };
        setSubmissionResults(results);
        
        setAssessmentPhase('results');
        setIsGrading(true);

          let pollCount = 0;
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = setInterval(async () => {
          pollCount++;
          try {
            const progressRes = assessmentType === 'quiz'
              ? await getQuizProgress(authData.id, courseId)
              : await getExamProgress(authData.id, courseId);
            if (progressRes.success) {
              const idField = assessmentType === 'quiz' ? 'quizzes' : 'exams';
              const itemId  = assessmentType === 'quiz' ? content.quizId : content.examId;
              const item    = progressRes.data?.[idField]?.find(i => i[assessmentType === 'quiz' ? 'quizId' : 'examId'] === itemId);
              const lastAttempt = item?.attempts?.[item.attempts.length - 1];
              const allGraded = lastAttempt?.answers?.every(a =>
                a.questionType === 'multiple_choice' ||
                a.questionType === 'true_false' ||
                a.aiGradeMetadata != null
              );
             
              if (allGraded || pollCount >= 15) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                setIsGrading(false);
                onAfterSubmit?.();
              }
            }
          } catch (err) {
            if (pollCount >= 15) {
              clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              setIsGrading(false);
              onAfterSubmit?.();
            }
          }
        }, 2000);

      } else if (assessmentType === 'exam') {
        const uploadedFileIds = await uploadEssayAttachments('exam', content.examId);
        const answerList = content.questions.map(question => {
          const questionId = question.questionId;
          const answer     = answers[questionId] || '';
          const answerObj  = { questionId, questionType: question.questionType || 'unknown', answer };
          if (uploadedFileIds[questionId]?.fileIds?.length > 0) {
            answerObj.attachmentFileIds   = uploadedFileIds[questionId].fileIds;
            answerObj.attachmentFileNames = uploadedFileIds[questionId].fileNames;
          }
          return answerObj;
        });

        const response = await submitExamAnswers(authData.id, {
          courseId, examId: content.examId, answers: answerList
        });
        if (!response.success) { showNotification(response.message || 'Submission failed', 'error'); return; }

        const results = {
          type: 'exam', title: content.title,
          questions: content.questions.map(q => {
            const studentAnswer = answers[q.questionId] || 'No answer';
            const needsGrading  = ['essay', 'code', 'short_answer', 'lab_practical'].includes(q.questionType);
            const isCorrect     = needsGrading
              ? null
              : studentAnswer.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase();
            return {
              questionId: q.questionId, questionText: q.questionText, questionType: q.questionType,
              options: q.options || [], studentAnswer, correctAnswer: q.correctAnswer || null,
              isCorrect, points: q.points || 0, earnedPoints: isCorrect ? (q.points || 0) : 0,
              explanation: q.explanation || null, needsGrading
            };
          }),
          totalPoints:  content.questions.reduce((sum, q) => sum + (q.points || 0), 0),
          earnedPoints: content.questions.reduce((sum, q) => {
            if (['essay', 'code'].includes(q.questionType)) return sum;
            const sa = answers[q.questionId] || '';
            return sum + (sa.trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase() ? (q.points || 0) : 0);
          }, 0),
          hasManualGrading: content.questions.some(q => ['essay', 'code'].includes(q.questionType))
        };
        setSubmissionResults(results);
        
        setAssessmentPhase('results');
        setIsGrading(true);

        let pollCount = 0;
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(async () => {
          pollCount++;
          try {
            const progressRes = assessmentType === 'quiz'
              ? await getQuizProgress(authData.id, courseId)
              : await getExamProgress(authData.id, courseId);
            if (progressRes.success) {
              const idField = assessmentType === 'quiz' ? 'quizzes' : 'exams';
              const itemId  = assessmentType === 'quiz' ? content.quizId : content.examId;
              const item    = progressRes.data?.[idField]?.find(i => i[assessmentType === 'quiz' ? 'quizId' : 'examId'] === itemId);
              const lastAttempt = item?.attempts?.[item.attempts.length - 1];
              const allGraded = lastAttempt?.answers?.every(a =>
                a.questionType === 'multiple_choice' ||
                a.questionType === 'true_false' ||
                a.aiGradeMetadata != null
              );

              if (allGraded || pollCount >= 15) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                setIsGrading(false);
                onAfterSubmit?.();
              }
            }
          } catch (err) {
            if (pollCount >= 15) {
              clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              setIsGrading(false);
              onAfterSubmit?.();
            }
          }
        }, 2000);
      }

    } catch (error) {
      console.error('Submission error:', error);
      showNotification('Failed to submit', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const calculateMaxPoints = (questions) => {
    if (!questions || questions.length === 0) return 0;
    return questions.reduce((total, question) => total + (parseFloat(question.points) || 0), 0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleRubric = (questionId) => {
    setExpandedRubrics(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FIELD / QUESTION HANDLERS — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════
  const handleFieldChange = (index, field, value) => {
    const updated = [...editedContent];
    updated[index][field] = value;
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  // const handleQuestionChange = (itemIndex, questionIndex, field, value) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions[questionIndex][field] = value;
  //   setEditedContent(updated);
  // };

  const handleQuestionChange = (itemIndex, questionIndex, field, value) => {
    const updated = [...editedContent];
    updated[itemIndex].questions[questionIndex][field] = value;
    setEditedContent(updated);
    editedContentRef.current = updated;
  };

  const handleQuestionTypeChange = (itemIndex, questionIndex, newType) => {
    const updated = [...editedContent];
    const question = updated[itemIndex].questions[questionIndex];
    const fieldsToRemove = ['options', 'aiGradingCriteria', 'evidence', 'labRequirements'];
    fieldsToRemove.forEach(field => { delete question[field]; });
    question.questionType = newType;
    switch (newType) {
      case 'multiple_choice':
        question.options = ['', '', '', ''];
        question.correctAnswer = '';
        break;
      case 'true_false':
      case 'short_answer':
      case 'essay':
      case 'code':
        question.correctAnswer = '';
        break;
      case 'lab_practical':
        question.correctAnswer = '';
        question.aiGradingCriteria = [];
        question.labRequirements = '';
        question.questionTitle = '';
        break;
      case 'performance':
        question.correctAnswer = '';
        break;
      default:
        question.correctAnswer = '';
    }
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  const handleOptionChange = (itemIndex, questionIndex, optionIndex, value) => {
    const updated = [...editedContent];
    updated[itemIndex].questions[questionIndex].options[optionIndex] = value;
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  const handleAddQuestion = (itemIndex) => {
    const updated = [...editedContent];
    const existingQuestions = updated[itemIndex].questions || [];
    let maxQuestionNum = 0;
    existingQuestions.forEach(q => {
      const match = q.questionId?.match(/Q(\d+)/);
      if (match) {
        const num = parseInt(match[1]);
        if (num > maxQuestionNum) maxQuestionNum = num;
      }
    });
    const newQuestion = {
      questionId: `Q${maxQuestionNum + 1}`,
      questionText: '',
      questionType: 'multiple_choice',
      options: ['', '', '', ''],
      points: 0,
    };
    if (!updated[itemIndex].questions) updated[itemIndex].questions = [];
    updated[itemIndex].questions.push(newQuestion);
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  const handleRemoveQuestion = (itemIndex, questionIndex) => {
    const updated = [...editedContent];
    updated[itemIndex].questions.splice(questionIndex, 1);
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  const handleAddOption = (itemIndex, questionIndex) => {
    const updated = [...editedContent];
    updated[itemIndex].questions[questionIndex].options.push('');
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  const handleRemoveOption = (itemIndex, questionIndex, optionIndex) => {
    const updated = [...editedContent];
    updated[itemIndex].questions[questionIndex].options.splice(optionIndex, 1);
    setEditedContent(updated);
    editedContentRef.current = updated; 
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EDIT / SAVE / CANCEL — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════
  const handleEditContent = () => {
    const cleanedItem = JSON.parse(JSON.stringify(content));
    if (cleanedItem.questions && Array.isArray(cleanedItem.questions)) {
      cleanedItem.questions = cleanedItem.questions.map(question => {
        const cleanQuestion = { ...question };
        switch (cleanQuestion.questionType) {
          case 'multiple_choice':
            delete cleanQuestion.aiGradingCriteria;
            delete cleanQuestion.evidence;
            break;
          case 'true_false':
          case 'short_answer':
          case 'essay':
          case 'code':
          case 'performance':
            delete cleanQuestion.options;
            delete cleanQuestion.aiGradingCriteria;
            delete cleanQuestion.evidence;
            break;
          case 'lab_practical':
            delete cleanQuestion.options;
            if (!cleanQuestion.aiGradingCriteria) cleanQuestion.aiGradingCriteria = [];
            if (!cleanQuestion.labRequirements) cleanQuestion.labRequirements = '';
            if (!cleanQuestion.questionTitle) cleanQuestion.questionTitle = '';
            break;
          default:
            delete cleanQuestion.options;
            delete cleanQuestion.aiGradingCriteria;
            delete cleanQuestion.evidence;
        }
        if (cleanQuestion.questionTitle === undefined) {
          cleanQuestion.questionTitle = '';
        }
        return cleanQuestion;
      });
    }
    setEditedContent([cleanedItem]);
    editedContentRef.current = [cleanedItem]; 
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditableRubric(null);
    setIsEditingRubric(false);
    setRubricPreview(null);
    setRubricQuestionIndex(null);
    setEditing(false);
    setEditedContent([JSON.parse(JSON.stringify(content))]);
    setEditedContentFiles({});
    editedContentRef.current = [JSON.parse(JSON.stringify(content))]; 
  };

  const validateContentBeforeSave = (contentType, items) => {
    const errors = [];
    
    items.forEach((item, idx) => {
      const itemNumber = idx + 1;
      
      if (contentType === 'lessons') {
        if (!item.title?.trim()) errors.push(`Lesson ${itemNumber}: Title is required`);
        if (!item.content?.trim()) errors.push(`Lesson ${itemNumber}: Content is required`);
        //if (item.order === undefined || item.order === null) errors.push(`Lesson ${itemNumber}: Order is required`);
      }
      
      if (contentType === 'labs') {
        if (!item.title?.trim()) errors.push(`Lab ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Lab ${itemNumber}: Description is required`);
        if (!String(item.dueDate || '').trim()) errors.push(`Lab ${itemNumber}: Lab Due Date is required`);
        if (!String(item.duration || '').trim()) errors.push(`Lab ${itemNumber}: Lab duration is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Lab ${itemNumber}: Grade Weight is required`);
        
        // Validate questions
        item.questions?.forEach((q, qIdx) => {
          const qNum = qIdx + 1;
          //if (!q.questionId?.trim()) errors.push(`Lab ${itemNumber}, Question ${qNum}: Question ID is required`);
          if (!q.questionText?.trim()) errors.push(`Lab ${itemNumber}, Question ${qNum}: Question Text is required`);
          if (q.questionType === 'lab_practical' && !q.questionTitle?.trim()) {
            errors.push(`Lab ${itemNumber}, Question ${qNum}: Question Title is required for Lab Practical questions`);
          }
          if (!q.questionType) errors.push(`Lab ${itemNumber}, Question ${qNum}: Question Type is required`);
          if (q.points === undefined || q.points === null || q.points === '') errors.push(`Lab ${itemNumber}, Question ${qNum}: Points is required`);
          
          // Correct answer required except for essay, code, performance
          if (!['essay', 'code', 'performance', 'lab_practical'].includes(q.questionType) && !q.correctAnswer?.trim()) {
            errors.push(`Lab ${itemNumber}, Question ${qNum}: Correct Answer is required`);
          }
        });
      }
      
      if (contentType === 'quizzes') {
        if (!item.title?.trim()) errors.push(`Quiz ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Quiz ${itemNumber}: Description is required`);
        if (!String(item.dueDate || '').trim()) errors.push(`Quiz ${itemNumber}: Quiz Due Date is required`);
        if (!String(item.duration || '').trim()) errors.push(`Quiz ${itemNumber}: Quiz duration is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Quiz ${itemNumber}: Grade Weight is required`);
        
        // Validate questions
        item.questions?.forEach((q, qIdx) => {
          const qNum = qIdx + 1;
          //if (!q.questionId?.trim()) errors.push(`Quiz ${itemNumber}, Question ${qNum}: Question ID is required`);
          if (!q.questionText?.trim()) errors.push(`Quiz ${itemNumber}, Question ${qNum}: Question Text is required`);
          if (!q.questionType) errors.push(`Quiz ${itemNumber}, Question ${qNum}: Question Type is required`);
          if (q.points === undefined || q.points === null || q.points === '') errors.push(`Quiz ${itemNumber}, Question ${qNum}: Points is required`);
          
          // Correct answer required except for essay, code
          if (!['essay', 'code'].includes(q.questionType) && !q.correctAnswer?.trim()) {
            errors.push(`Quiz ${itemNumber}, Question ${qNum}: Correct Answer is required`);
          }
        });
      }
      
      if (contentType === 'exams') {
        if (!item.title?.trim()) errors.push(`Exam ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Exam ${itemNumber}: Description is required`);
        if (!String(item.dueDate || '').trim()) errors.push(`Exam ${itemNumber}: Exam Due Date is required`);
        if (!String(item.duration || '').trim()) errors.push(`Exam ${itemNumber}: Exam duration is required`);
        if (!item.duration) errors.push(`Exam ${itemNumber}: Duration is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Exam ${itemNumber}: Grade Weight is required`);
        
        // Validate questions
        item.questions?.forEach((q, qIdx) => {
          const qNum = qIdx + 1;
          //if (!q.questionId?.trim()) errors.push(`Exam ${itemNumber}, Question ${qNum}: Question ID is required`);
          if (!q.questionText?.trim()) errors.push(`Exam ${itemNumber}, Question ${qNum}: Question Text is required`);
          if (!q.questionType) errors.push(`Exam ${itemNumber}, Question ${qNum}: Question Type is required`);
          if (q.points === undefined || q.points === null || q.points === '') errors.push(`Exam ${itemNumber}, Question ${qNum}: Points is required`);
          
          // Correct answer required except for essay, code
          if (!['essay', 'code'].includes(q.questionType) && !q.correctAnswer?.trim()) {
            errors.push(`Exam ${itemNumber}, Question ${qNum}: Correct Answer is required`);
          }
        });
      }
      
      if (contentType === 'capstones') {
        if (!item.capstoneId?.trim()) errors.push(`Capstone ${itemNumber}: Capstone ID is required`);
        if (!item.title?.trim()) errors.push(`Capstone ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Capstone ${itemNumber}: Description is required`);
        if (item.maxGrade === undefined || item.maxGrade === null) errors.push(`Capstone ${itemNumber}: Max Points is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Capstone ${itemNumber}: Grade Weight is required`);
      }
    });
    
    return errors;
  };

  const uploadContentFiles = async (contentType, contentId) => {
    const files = editedContentFiles[contentType];
    
    if (!files || files.length === 0) {
      return { fileIds: [], fileNames: [] };
    }

    setUploadingContentFiles(true);
    const uploadedFileIds = [];
    const uploadedFileNames = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (file.size > 10 * 1024 * 1024) {
          showNotification(`File ${file.name} exceeds 10MB limit`, 'error');
          continue;
        }

        const allowedTypes = ['application/pdf', 'application/msword', 
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                            'text/plain', 'image/png', 'image/jpeg', 'application/zip'];
        if (!allowedTypes.includes(file.type)) {
          showNotification(`File ${file.name} has invalid type`, 'error');
          continue;
        }

        setContentFileUploadProgress(prev => ({
          ...prev,
          [contentType]: `Uploading ${i + 1}/${files.length}...`
        }));

        const fileExtension = file.name.split('.').pop();
        const structuredFilename = `${authData.id}/${courseId}/${contentType}/${contentId}/resource_${Date.now()}_${i + 1}.${fileExtension}`;

        const metadata = {
          instructorId: authData.id,
          courseId: courseId,
          contentType: contentType,
          contentId: contentId,
          uploadedBy: authData.id,
          originalFilename: file.name,
          structuredFilename: structuredFilename
        };

        const uploadResult = await uploadFile(file, metadata);

        if (uploadResult.success) {
          uploadedFileIds.push(uploadResult.data.fileId);
          uploadedFileNames.push(file.name);
        } else {
          showNotification(`Failed to upload ${file.name}`, 'error');
        }
      }

      setContentFileUploadProgress(prev => ({
        ...prev,
        [contentType]: 'Complete'
      }));

      return { fileIds: uploadedFileIds, fileNames: uploadedFileNames };
    } catch (error) {
      console.error('Error uploading content files:', error);
      showNotification('Failed to upload some files', 'error');
      return { fileIds: uploadedFileIds, fileNames: uploadedFileNames };
    } finally {
      setUploadingContentFiles(false);
      setContentFileUploadProgress({});
    }
  };

  const handleSaveEdit = async () => {
    if (!editedContentRef.current || !selectedContentType || editedContentRef.current.length === 0) {
    // if (!editedContent || !selectedContentType || editedContent.length === 0) {
      showNotification('No changes to save', 'info');
      return;
    }

    // ✅ VALIDATE BEFORE SAVING
    // const validationErrors = validateContentBeforeSave(selectedContentType, editedContent);
    const validationErrors = validateContentBeforeSave(selectedContentType, editedContentRef.current);
    if (validationErrors.length > 0) {
      const errorMessage = validationErrors.join('\n');
      setValidationAlertMessage(`Please fill out all required fields:\n\n${errorMessage}`);
      setValidationAlert(true);
      return;
    }

    try {
      const contentTypeMap = {
        'lessons': { singular: 'lesson', idField: 'lessonId' },
        'labs': { singular: 'lab', idField: 'labId' },
        'quizzes': { singular: 'quiz', idField: 'quizId' },
        'exams': { singular: 'exam', idField: 'examId' },
        'capstones': { singular: 'capstone', idField: 'capstoneId' }
      };

      const { singular: contentType, idField } = contentTypeMap[selectedContentType];

      // Since we're only editing ONE item, editedContent[0] is that item
      // const item = editedContent[0];
      const item = editedContentRef.current[0];
      const contentId = item[idField];
      
      // Prepare update data (remove the ID field from updates)
      const { [idField]: _, ...updateData } = item;
      
      // Add updatedBy field
      updateData.updatedBy = authData.id;

      // ✅ NEW: Upload files if any (only for lessons and labs)
      if (selectedContentType === 'lessons' || selectedContentType === 'labs') {
        const uploadResult = await uploadContentFiles(selectedContentType, contentId);
        
        if (uploadResult.fileIds.length > 0) {
          // Add uploaded files to existing files
          const existingFileIds = updateData.resourceFileIds || [];
          const existingFileNames = updateData.resourceFileNames || [];
          
          updateData.resourceFileIds = [...existingFileIds, ...uploadResult.fileIds];
          updateData.resourceFileNames = [...existingFileNames, ...uploadResult.fileNames];
        }
      }

      // ✅ ADD THIS: Convert numeric fields from strings to numbers
      if (updateData.duration !== undefined) {
        updateData.duration = Number(updateData.duration);
      }
      if (updateData.maxGrade !== undefined) {
        updateData.maxGrade = Number(updateData.maxGrade);
      }
      if (updateData.gradeWeight !== undefined) {
        updateData.gradeWeight = Number(updateData.gradeWeight);
      }
      if (updateData.attemptsAllowed !== undefined) {
        updateData.attemptsAllowed = Number(updateData.attemptsAllowed);
      }
      if (updateData.order !== undefined) {
        updateData.order = Number(updateData.order);
      }

      if (selectedContentType === 'labs' || selectedContentType === 'quizzes' || selectedContentType === 'exams') {
        if (updateData.questions && updateData.questions.length > 0) {
          updateData.maxGrade = calculateMaxPoints(updateData.questions);
        }
      }
      
      // Call the API to update this specific content item
      const response = await updateCourseContent(
        courseId,
        contentType,
        contentId,
        updateData
      );

      if (response.success) {
        showNotification(
          `${contentType.charAt(0).toUpperCase() + contentType.slice(1)} updated successfully`,
          'success'
        );
        setEditing(false);
        setEditedContent(null);
        setEditedContentFiles({});  // ✅ Clear uploaded files
        editedContentRef.current = null; 
        await onLoad(); // Reload the data
      } else {
        showNotification('Failed to update content', 'error');
      }
    } catch (error) {
      console.error('=== SAVE ERROR ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      showNotification('Failed to update content', 'error');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RUBRIC HANDLERS — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════

  const calculateTotalPercentage = () => {
    if (!editableRubric?.rubric) return 0;
    return Object.values(editableRubric.rubric).reduce((sum, criterion) => sum + (criterion.percentage || 0), 0);
  };

  const validateRubric = () => {
    const totalPercentage = calculateTotalPercentage();
    if (totalPercentage !== 100) {
      showNotification(`Total percentage must equal 100% (currently ${totalPercentage}%)`, 'error');
      return false;
    }
    for (const [key, criterion] of Object.entries(editableRubric.rubric)) {
      if (!criterion.description || !criterion.fullCredit || !criterion.noCredit) {
        showNotification(`Criterion "${key}" is missing required fields`, 'error');
        return false;
      }
    }
    return true;
  };

  const handleGenerateRubric = async (itemIdx, questionIdx) => {
    const question = editedContent[itemIdx].questions[questionIdx];
    if (!question.questionText || !question.points) {
      showNotification('Please fill in question text and points first', 'error');
      return;
    }
    setGeneratingRubric(true);
    setRubricQuestionIndex({ itemIdx, questionIdx });
    try {
      const response = await generateQuestionRubric(
        authData?.id,
        {
          questionText:   question.questionText,
          questionType:   question.questionType,
          expectedAnswer: question.correctAnswer || '',
          maxPoints:      question.points
        }
      );
      if (response.success) {
        setRubricPreview(response.data);
        setEditableRubric(JSON.parse(JSON.stringify(response.data)));
        setIsEditingRubric(true);
        showNotification('Rubric generated successfully!', 'success');
      } else {
        showNotification('Failed to generate rubric', 'error');
      }
    } catch (error) {
      console.error('Rubric generation error:', error);
      showNotification('Error generating rubric', 'error');
    } finally {
      setGeneratingRubric(false);
    }
  };

  const handleCriterionPercentageChange = (criterionKey, newPercentage) => {
    const percentage = parseInt(newPercentage) || 0;
    setEditableRubric(prev => {
      const updated = { ...prev };
      const maxPoints = editedContent[rubricQuestionIndex.itemIdx].questions[rubricQuestionIndex.questionIdx].points;
      updated.rubric[criterionKey].percentage = percentage;
      updated.rubric[criterionKey].max = parseFloat(((percentage / 100) * maxPoints).toFixed(2));
      return updated;
    });
  };

  const handleCriterionFieldChange = (criterionKey, field, value) => {
    setEditableRubric(prev => {
      const updated = { ...prev };
      updated.rubric[criterionKey][field] = value;
      return updated;
    });
  };

  const handleCriterionNameChange = (oldKey, newKey) => {
    const sanitizedKey = newKey.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!sanitizedKey || sanitizedKey === oldKey) return;
    if (editableRubric.rubric[sanitizedKey]) {
      showNotification('Criterion name already exists', 'error');
      return;
    }
    setEditableRubric(prev => {
      const updated = { ...prev };
      updated.rubric[sanitizedKey] = { ...updated.rubric[oldKey] };
      delete updated.rubric[oldKey];
      return updated;
    });
  };

  const handleAddCriterion = () => {
    let criterionName = 'new_criterion';
    let counter = 1;
    while (editableRubric.rubric[criterionName]) {
      criterionName = `new_criterion_${counter}`;
      counter++;
    }
    setEditableRubric(prev => {
      const updated = { ...prev };
      updated.rubric[criterionName] = {
        max: 0, percentage: 0,
        description:      'Description for this criterion',
        fullCredit:       'Criteria for full credit',
        partialCreditHigh:'Criteria for high partial credit (70-85%)',
        partialCreditLow: 'Criteria for low partial credit (40-65%)',
        noCredit:         'Criteria for no credit'
      };
      return updated;
    });
  };

  const handleRemoveCriterion = (criterionKey) => {
    if (Object.keys(editableRubric.rubric).length <= 1) {
      showNotification('Cannot remove the last criterion', 'error');
      return;
    }
    setEditableRubric(prev => {
      const updated = { ...prev };
      delete updated.rubric[criterionKey];
      return updated;
    });
  };

  const handleRubricTextChange = (newText) => {
    setEditableRubric(prev => ({ ...prev, rubricText: newText }));
  };

  const handleResetToOriginal = () => {
    if (rubricPreview) {
      setEditableRubric(JSON.parse(JSON.stringify(rubricPreview)));
      showNotification('Reset to AI-generated rubric', 'info');
    }
  };

  const handleEditRubric = (question, itemIdx, qIdx) => {
    // Initialize editedContent from content so handleAcceptRubric can write back
    if (!editedContent) {
      setEditedContent([JSON.parse(JSON.stringify(content))]);
      editedContentRef.current = [JSON.parse(JSON.stringify(content))]; 
    }
    setRubricQuestionIndex({ itemIdx, questionIdx: qIdx });
    setEditableRubric(JSON.parse(JSON.stringify(question.aiGradeMetadata)));
    setIsEditingRubric(true);
    setTimeout(() => {
      document.querySelector('.rubric-editor-box')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleAcceptRubric = () => {
    if (!validateRubric()) return;
    const { itemIdx, questionIdx } = rubricQuestionIndex;
    const updatedContent = [...editedContent];
    updatedContent[itemIdx].questions[questionIdx].aiGradeMetadata = {
      rubric:       editableRubric.rubric,
      rubricText:   editableRubric.rubricText,
      questionType: editableRubric.questionType,
      maxPoints:    editableRubric.maxPoints
    };
    setEditedContent(updatedContent);
    editedContentRef.current = updatedContent; 
    setEditableRubric(null);
    setIsEditingRubric(false);
    setRubricPreview(null);
    setRubricQuestionIndex(null);
    showNotification('Rubric applied successfully!', 'success');
  };

  const handleRejectRubric = () => {
    setEditableRubric(null);
    setIsEditingRubric(false);
    setRubricPreview(null);
    setRubricQuestionIndex(null);
    showNotification('Rubric discarded', 'info');
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FILE UPLOAD SECTION — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════

  const renderFileUploadSection = (contentTypeKey, existingFileIds, existingFileNames) => (
    <div className="am-edit-field">
      <label className="am-edit-label">📎 Attach Resource Files (PDF, DOC, PPT, Images, ZIP)</label>
      <div className="file-upload-section">
        <label className="file-upload-label">
          <input
            type="file"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.zip"
            multiple
            onChange={(e) => onUploadFiles(contentTypeKey, e.target.files)}
            className="file-input-hidden"
          />
          <span className="file-upload-button">Choose Files</span>
        </label>
        <span className="file-upload-hint">Max 10MB per file</span>
      </div>

      {editedContentFiles?.[contentTypeKey]?.length > 0 && (
        <div className="uploaded-files-list">
          <strong>New Files to Upload:</strong>
          {Array.from(editedContentFiles[contentTypeKey]).map((file, fileIdx) => (
            <div key={fileIdx} className="uploaded-file-item">
              <span className="file-icon">📄</span>
              <span className="file-name">{file.name}</span>
              <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
              <button type="button" onClick={() => onRemoveNewFile(contentTypeKey, fileIdx)} className="am-btn am-btn--sm am-btn--danger">✕</button>
            </div>
          ))}
        </div>
      )}

      {existingFileIds?.length > 0 && (
        <div className="existing-files-list">
          <strong>Existing Files:</strong>
          {existingFileIds.map((fileId, fileIdx) => (
            <div key={fileIdx} className="uploaded-file-item">
              <span className="file-icon">📄</span>
              <span className="file-name">{existingFileNames?.[fileIdx] || `File ${fileIdx + 1}`}</span>
              <button type="button" onClick={() => onDownload(fileId, existingFileNames?.[fileIdx])} className="am-btn am-btn--sm am-btn--ghost">⬇️ Download</button>
              <button
                type="button"
                onClick={() => {
                  const updatedIds   = [...existingFileIds];
                  const updatedNames = [...(existingFileNames || [])];
                  updatedIds.splice(fileIdx, 1);
                  updatedNames.splice(fileIdx, 1);
                  handleFieldChange(idx, 'resourceFileIds',   updatedIds);
                  handleFieldChange(idx, 'resourceFileNames', updatedNames);
                }}
                className="am-btn am-btn--sm am-btn--danger"
              >🗑️</button>
            </div>
          ))}
        </div>
      )}

      {uploadingContentFiles && contentFileUploadProgress?.[contentTypeKey] && (
        <div className="am-upload-banner">
          <span className="am-upload-spin" />
          <span>{contentFileUploadProgress[contentTypeKey]}</span>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER RUBRIC VIEW — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════

  const renderRubricView = (question, itemIdx, qIdx) => {
    if (!['essay', 'code', 'short_answer'].includes(question.questionType)) return null;
    if (!question.aiGradeMetadata) return null;

    const rubric = question.aiGradeMetadata.rubric;
    const hasRubricCriteria = rubric && typeof rubric === 'object' && Object.keys(rubric).length > 0;

    return (
      <div className="am-rubric-panel" style={{ marginTop: '10px' }}>
        <div className="am-rubric-panel-header" onClick={() => toggleRubric(question.questionId)} style={{ cursor: 'pointer' }}>
          <strong>🤖 Prof-iX Grading Rubric</strong>
          <button type="button" className="am-btn am-btn--sm am-btn--rubric-view">
            {expandedRubrics[question.questionId] ? '▲ Hide' : '▼ View'}
          </button>
        </div>

        {expandedRubrics[question.questionId] && (
          <div className="am-rubric-full">
            {question.aiGradeMetadata.rubricText && (
              <div className="am-answer-box" style={{ marginBottom: '12px' }}>
                <strong>SUMMARY:</strong>
                <p style={{ margin: '4px 0 0' }}>{question.aiGradeMetadata.rubricText}</p>
              </div>
            )}

            {hasRubricCriteria ? (
              <>
                <span className="am-section-title">Grading Criteria</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                  {Object.entries(rubric).map(([criterionKey, criterion]) => (
                    <div key={criterionKey} className="am-section" style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--m-text-primary)' }}>
                          {criterionKey.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className="am-points-badge">
                          {criterion.max} pts ({question.points ? ((criterion.max / question.points) * 100).toFixed(0) : '—'}%)
                        </span>
                      </div>
                      {role === 'student' ? (<div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--m-text-secondary)' }}>
                        {criterion.description   && <p style={{ margin: 0 }}><strong>📋 Description:</strong> {criterion.description}</p>}
                      </div>
                      ):(
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--m-text-secondary)' }}>
                        {criterion.description   && <p style={{ margin: 0 }}><strong>📋 Description:</strong> {criterion.description}</p>}
                        {criterion.fullCredit    && <p style={{ margin: 0 }}><strong style={{ color: 'var(--m-green)' }}>✓ Full Credit:</strong> {criterion.fullCredit}</p>}
                        {criterion.partialCreditHigh && <p style={{ margin: 0 }}><strong style={{ color: 'var(--m-amber)' }}>◐ Partial High:</strong> {criterion.partialCreditHigh}</p>}
                        {criterion.partialCreditLow  && <p style={{ margin: 0 }}><strong style={{ color: 'var(--m-amber)' }}>◑ Partial Low:</strong> {criterion.partialCreditLow}</p>}
                        {criterion.noCredit      && <p style={{ margin: 0 }}><strong style={{ color: 'var(--m-red)' }}>✗ No Credit:</strong> {criterion.noCredit}</p>}
                      </div>)}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="am-text" style={{ color: 'var(--m-text-faint)', fontStyle: 'italic' }}>
                No rubric criteria defined — AI grades holistically.
              </p>
            )}

            {role === 'instructor' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="button" className="am-btn am-btn--sm am-btn--rubric-view"
                  onClick={() => handleEditRubric(question, itemIdx, qIdx)}>
                  ✏️ Edit Rubric
                </button>
              </div>
            )}
          </div>
        )}
        {/* Rubric editor — shown inline when instructor clicks Edit Rubric */}
        {role === 'instructor' && isEditingRubric &&
        rubricQuestionIndex?.itemIdx === itemIdx &&
        rubricQuestionIndex?.questionIdx === qIdx && (
          <div className="rubric-editor-box">
            {/* ...same rubric-editor-box JSX already in renderRubricGenerator... */}
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER RUBRIC GENERATOR — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════

  const renderRubricGenerator = (question, itemIdx, qIdx) => {
    if (!['essay', 'code', 'short_answer'].includes(question.questionType)) return null;

    const isCurrentQuestion = rubricQuestionIndex?.itemIdx === itemIdx && rubricQuestionIndex?.questionIdx === qIdx;
    const totalPercentage   = isCurrentQuestion && editableRubric ? calculateTotalPercentage() : 0;

    return (
      <div className="am-rubric-generator-wrap">
        {(!isEditingRubric || !isCurrentQuestion) && (
          <>
            {question.aiGradeMetadata &&
              <span className="am-status-chip am-status-chip--graded">✓ AI Rubric Applied</span>
            }
            <button
              type="button"
              onClick={() => handleGenerateRubric(itemIdx, qIdx)}
              disabled={generatingRubric || !question.questionText || !question.points}
              className="am-btn am-btn--sm am-btn--rubric-gen"
            >
              {generatingRubric && isCurrentQuestion
                ? '⏳ Generating...'
                : (!question.aiGradeMetadata ? `🤖 Generate Rubric` : '🤖 Regenerate Rubric')
              }
            </button>
          </>
        )}

        {isEditingRubric && isCurrentQuestion && editableRubric && (
          <div className="rubric-editor-box">
            <div className="rubric-editor-header">
              <h5>📝 Edit Rubric Generated by Prof_iX</h5>
              <span className="edit-hint">Customize before accepting</span>
            </div>

            <div className="am-edit-field">
              <label className="am-edit-label">📋 Summary Description:</label>
              <textarea
                value={editableRubric.rubricText || ''}
                onChange={(e) => handleRubricTextChange(e.target.value)}
                className="am-edit-textarea" rows="3"
                placeholder="Overall rubric description..."
              />
            </div>

            <div className="rubric-criteria-editor">
              <label className="am-edit-label">Grading Criteria:</label>
              {Object.entries(editableRubric.rubric || {}).map(([criterionKey, criterion]) => (
                <div key={criterionKey} className="criterion-editor-card">
                  <div className="criterion-header">
                    <input
                      type="text"
                      value={criterionKey.replace(/_/g, ' ').toUpperCase()}
                      onChange={(e) => {
                        const newKey = e.target.value.toLowerCase().replace(/\s+/g, '_');
                        if (newKey !== criterionKey) handleCriterionNameChange(criterionKey, newKey);
                      }}
                      className="criterion-name-input"
                      placeholder="Criterion name"
                    />
                    <button type="button" onClick={() => handleRemoveCriterion(criterionKey)} className="am-btn am-btn--sm am-btn--danger" title="Remove criterion">🗑️</button>
                  </div>

                  <div className="criterion-field">
                    <label className="am-edit-label">Weight/Percentage:</label>
                    <div className="percentage-slider-container">
                      <input type="range" min="0" max="100" value={criterion.percentage || 0}
                        onChange={(e) => handleCriterionPercentageChange(criterionKey, e.target.value)}
                        className="percentage-slider"/>
                      <input type="number" min="0" max="100" value={criterion.percentage || 0}
                        onChange={(e) => handleCriterionPercentageChange(criterionKey, e.target.value)}
                        className="am-edit-input" style={{ width: '70px' }}/>
                      <span className="percentage-label">%</span>
                      <span className="points-display">({criterion.max || 0} points)</span>
                    </div>
                  </div>

                  {[
                    ['description',       '📋 Description'],
                    ['fullCredit',         'Full Credit Criteria'],
                    ['partialCreditHigh',  'Partial Credit High (70-85%)'],
                    ['partialCreditLow',   'Partial Credit Low (40-65%)'],
                    ['noCredit',           'No Credit Criteria'],
                  ].map(([field, label]) => (
                    <div key={field} className="criterion-field">
                      <label className="am-edit-label">{label}:</label>
                      <textarea
                        value={criterion[field] || ''}
                        onChange={(e) => handleCriterionFieldChange(criterionKey, field, e.target.value)}
                        className="am-edit-textarea" rows="2"/>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <button type="button" onClick={handleAddCriterion} className="am-btn am-btn--sm am-btn--add">➕ Add Criterion</button>

            <div className={`rubric-validation ${totalPercentage === 100 ? 'valid' : 'invalid'}`}>
              <span>Total: {totalPercentage}% {totalPercentage === 100 ? ' ✓' : ' ⚠️ Must equal 100%'}</span>
            </div>

            <div className="rubric-editor-actions">
              <button type="button" onClick={handleResetToOriginal} className="am-btn am-btn--sm am-btn--ghost">🔄 Reset to Original</button>
              <button type="button" onClick={handleAcceptRubric} disabled={totalPercentage !== 100} className="am-btn am-btn--sm am-btn--success">✓ Accept & Apply Rubric</button>
              <button type="button" onClick={handleRejectRubric} className="am-btn am-btn--sm am-btn--danger">✕</button>
            </div>
          </div>
        )}

        <LabGradingRubric
          labAIGradingCriteria={question.aiGradingCriteria}
          questionId={question.questionId}
          isExpanded={expandedRubrics[question.questionId]}
          onToggle={toggleRubric}
          role={role}
          onEditRubric={() => handleEditRubric(question, itemIdx, qIdx)}
        />
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER Lab REQUIREMENTS BUILDER — from InstructorCourseManagement
  // ═══════════════════════════════════════════════════════════════════════════

  const renderLabRequirementsBuilder = (question, idx, qIdx) => {

    const handleAIGenerate = async () => {
      if (!question.questionText?.trim() || !question.labRequirements?.trim()) {
        showNotification('Please fill in Lab Objective and Lab Requirements first', 'error');
        return;
      }
      setIsGeneratingRequirements(true);
      setGenerationRequirementError(null);
      try {
        const response = await generateCompleteLabRequirements(
          question.questionText,
          question.labRequirements,
          question.points || 0,
          question.domain || 'IoT'
        );
        if (response.success) {
          setGeneratedPreview({ data: response.data, targetIdx: idx, targetQIdx: qIdx });
          showNotification(`${response.data.length} requirements generated! Review and edit before accepting.`, 'success');
        } else {
          setGenerationRequirementError(response.error || 'Failed to generate requirements');
          showNotification(response.error || 'Failed to generate requirements', 'error');
        }
      } catch (error) {
        setGenerationRequirementError(error.message);
        showNotification('AI generation failed', 'error');
      } finally {
        setIsGeneratingRequirements(false);
      }
    };

    const handleAcceptAI = () => {
      console.log(generatedPreview);

      if (!generatedPreview || !Array.isArray(generatedPreview.data)) return;
      const { data, targetIdx, targetQIdx } = generatedPreview;
      const newRequirements = data.map((req, i) => ({ ...req, id: `R${i + 1}` }));
      const totalPoints = newRequirements.reduce((sum, r) => sum + (r.points || 0), 0);
      handleQuestionChange(targetIdx, targetQIdx, 'aiGradingCriteria', newRequirements);
      handleQuestionChange(targetIdx, targetQIdx, 'points', totalPoints);
      setGeneratedPreview(null);
      setGenerationRequirementError(null);
      showNotification(`${newRequirements.length} requirements added! Total: ${totalPoints} points.`, 'success');
    };

    const handleRejectAI = () => {
      setGeneratedPreview(null);
      setGenerationRequirementError(null);
    };

    const handlePreviewFieldChange = (previewIdx, field, value) => {
      setGeneratedPreview(prev => {
        const updated = [...prev.data];
        updated[previewIdx] = { ...updated[previewIdx], [field]: value };
        if (field === 'percentage') {
          const totalPts = question.points || 0;
          updated[previewIdx].points = Math.round((value / 100) * totalPts * 100) / 100;
        }
        return { ...prev, data: updated };
      });
    };

    const handlePreviewRubricChange = (previewIdx, field, value) => {
      setGeneratedPreview(prev => {
        const updated = [...prev.data];
        updated[previewIdx] = { ...updated[previewIdx], rubric: { ...updated[previewIdx].rubric, [field]: value } };
        return { ...prev, data: updated };
      });
    };

    const handlePreviewTierChange = (previewIdx, tierIdx, field, value) => {
      setGeneratedPreview(prev => {
        const updated = [...prev.data];
        const newPartial = [...(updated[previewIdx].rubric?.partial || [])];
        newPartial[tierIdx] = {
          ...newPartial[tierIdx],
          [field]: field === 'threshold' || field === 'pointsPercentage' ? Number(value) : value,
          ...(field === 'threshold' ? { pointsPercentage: Number(value) / 100 } : {})
        };
        updated[previewIdx] = { ...updated[previewIdx], rubric: { ...updated[previewIdx].rubric, partial: newPartial } };
        return { ...prev, data: updated };
      });
    };

    const handlePreviewRemoveTier = (previewIdx, tierIdx) => {
      setGeneratedPreview(prev => {
        const updated = [...prev.data];
        const newPartial = [...(updated[previewIdx].rubric?.partial || [])];
        newPartial.splice(tierIdx, 1);
        updated[previewIdx] = { ...updated[previewIdx], rubric: { ...updated[previewIdx].rubric, partial: newPartial } };
        return { ...prev, data: updated };
      });
    };

    const handlePreviewAddTier = (previewIdx) => {
      setGeneratedPreview(prev => {
        const updated = [...prev.data];
        const newPartial = [...(updated[previewIdx].rubric?.partial || [])];
        newPartial.push({ threshold: 70, description: '', pointsPercentage: 0.7 });
        updated[previewIdx] = { ...updated[previewIdx], rubric: { ...updated[previewIdx].rubric, partial: newPartial } };
        return { ...prev, data: updated };
      });
    };

    const handleRequirementFieldChange = (reqIdx, field, value) => {
      const updated = [...(question.aiGradingCriteria || [])];
      updated[reqIdx] = { ...updated[reqIdx], [field]: value };
      if (field === 'percentage') {
        updated[reqIdx].points = Math.round((value / 100) * question.points * 100) / 100;
      }
      handleQuestionChange(idx, qIdx, 'aiGradingCriteria', updated);
      if (field === 'points') {
        const totalPoints = updated.reduce((sum, r) => sum + (parseFloat(r.points) || 0), 0);
        handleQuestionChange(idx, qIdx, 'points', totalPoints);
      }
    };

    const handleRubricFieldChange = (reqIdx, field, value) => {
      const updated = [...(question.aiGradingCriteria || [])];
      updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, [field]: value } };
      handleQuestionChange(idx, qIdx, 'aiGradingCriteria', updated);
    };

    const handleRubricPartialChange = (reqIdx, tierIdx, field, value) => {
      const updated = [...(question.aiGradingCriteria || [])];
      const newPartial = [...(updated[reqIdx].rubric?.partial || [])];
      newPartial[tierIdx] = {
        ...newPartial[tierIdx],
        [field]: field === 'threshold' || field === 'pointsPercentage' ? Number(value) : value,
        ...(field === 'threshold' ? { pointsPercentage: Number(value) / 100 } : {})
      };
      updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, partial: newPartial } };
      handleQuestionChange(idx, qIdx, 'aiGradingCriteria', updated);
    };

    const previewTotalPercentage = generatedPreview?.data?.reduce((sum, r) => sum + (r.percentage || 0), 0) || 0;
    return (
      <div className="am-edit-field iot-requirements-builder">
        <label className="am-edit-label">🔡 Lab Requirements / Grading Criteria:</label>
        <p className="field-hint">Define requirements for AI grading of practical lab sessions</p>

        <div className="ai-assistant-section">
          <div className="ai-assistant-header">
            <h6>✨ AI Assistant - Generate Requirements & Rubrics</h6>
            <span className="ai-badge">Powered by OpenAI</span>
          </div>

          <div className="ai-assistant-form">
            <div className="ai-form-column">
              <div className="ai-form-input-field">
                <label className="am-edit-label">Lab Objective (from question above):</label>
                <textarea value={question.questionText || ''} className="am-edit-textarea" rows={2} readOnly
                  style={{ backgroundColor: 'var(--m-bg-elevated)', cursor: 'not-allowed', color: 'var(--m-text-faint)' }}/>
              </div>
              <div className="ai-form-input-field">
                <label className="am-edit-label">Lab Requirements (from field above):</label>
                <textarea value={question.labRequirements || ''} className="am-edit-textarea" rows={4} readOnly
                  style={{ backgroundColor: 'var(--m-bg-elevated)', cursor: 'not-allowed', color: 'var(--m-text-faint)' }}/>
              </div>
              <div className="ai-form-field" style={{ width: '200px' }}>
                <label className="am-edit-label">Total Points (from question above):</label>
                <input type="number" value={question.points || 0} className="am-edit-input" readOnly
                  style={{ backgroundColor: 'var(--m-bg-elevated)', cursor: 'not-allowed', color: 'var(--m-text-faint)' }}/>
              </div>
            </div>

            <button type="button" onClick={handleAIGenerate} className="am-btn am-btn--rubric-gen"
              disabled={
                isGeneratingRequirements ||
                (!!generatedPreview && generatedPreview.targetIdx === idx && generatedPreview.targetQIdx === qIdx) ||
                !question.questionText?.trim() ||
                !question.labRequirements?.trim() ||
                !question.points
              }
            >
              {isGeneratingRequirements
                ? <><span className="am-upload-spin" style={{ width: 14, height: 14 }} /> Generating...</>
                : (question.aiGradingCriteria?.length > 0
                    ? <>🔄 Regenerate All Requirements with AI</>
                    : <>✨ Generate All Requirements with AI</>
                  )
              }
            </button>

            {!question.points && (
              <p style={{ color: 'var(--m-red)', fontSize: '12px', marginTop: '6px' }}>
                ⚠️ Set Total Points on the question before generating
              </p>
            )}
          </div>

          {generationRequirementError && (
            <div className="am-upload-banner" style={{ borderColor: 'var(--m-red-border)', background: 'var(--m-red-dim)' }}>
              <span>⚠️</span>
              <span>{generationRequirementError}</span>
            </div>
          )}

          {generatedPreview && generatedPreview.targetIdx === idx && generatedPreview.targetQIdx === qIdx && Array.isArray(generatedPreview.data) && (
            <div className="rubric-editor-box">
              <div className="rubric-editor-header">
                <h5>📝 Edit AI-Generated Requirements</h5>
                <span className="edit-hint">Adjust percentages and rubric text before accepting</span>
              </div>
              <div className={`rubric-validation ${previewTotalPercentage === 100 ? 'valid' : 'invalid'}`}>
                <span>Total: {previewTotalPercentage}%{previewTotalPercentage === 100 ? ' ✓' : ' ⚠️ Must equal 100%'}</span>
              </div>
              {generatedPreview.data.map((req, previewIdx) => (
                <div key={previewIdx} className="criterion-editor-card">
                  <div className="criterion-header">
                    <span style={{ fontWeight: 600 }}>Requirement {req.requirementIndex}</span>
                  </div>
                  <div className="criterion-field">
                    <label className="am-edit-label">📋 Description:</label>
                    <textarea value={req.description || ''} onChange={(e) => handlePreviewFieldChange(previewIdx, 'description', e.target.value)} className="am-edit-textarea" rows={2}/>
                  </div>
                  <div className="criterion-field">
                    <label className="am-edit-label">Weight / Percentage:</label>
                    <div className="percentage-slider-container">
                      <input type="range" min="0" max="100" value={req.percentage || 0}
                        onChange={(e) => handlePreviewFieldChange(previewIdx, 'percentage', Number(e.target.value))} className="percentage-slider"/>
                      <input type="number" min="0" max="100" value={req.percentage || 0}
                        onChange={(e) => handlePreviewFieldChange(previewIdx, 'percentage', Number(e.target.value))} className="am-edit-input" style={{ width: '70px' }}/>
                      <span className="percentage-label">%</span>
                      <span className="points-display">({req.points || 0} points)</span>
                    </div>
                  </div>
                  <div className="criterion-field">
                    <label className="am-edit-label">✓ Full Credit:</label>
                    <textarea value={req.rubric?.full || ''} onChange={(e) => handlePreviewRubricChange(previewIdx, 'full', e.target.value)} className="am-edit-textarea" rows={2}/>
                  </div>
                  <div className="criterion-field">
                    <label className="am-edit-label">◐ Partial Credit Tiers:</label>
                    {(req.rubric?.partial || []).map((tier, tIdx) => (
                      <div key={tIdx} className="rubric-tier-edit-row">
                        <input type="number" value={tier.threshold || 0} onChange={(e) => handlePreviewTierChange(previewIdx, tIdx, 'threshold', e.target.value)} placeholder="Threshold %" className="am-edit-input" style={{ width: '90px' }} min="0" max="100"/>
                        <textarea value={tier.description || ''} onChange={(e) => handlePreviewTierChange(previewIdx, tIdx, 'description', e.target.value)} placeholder="Description" className="am-edit-textarea" rows={2} style={{ flex: 1 }}/>
                        <button type="button" onClick={() => handlePreviewRemoveTier(previewIdx, tIdx)} className="am-btn am-btn--sm am-btn--danger">✕</button>
                      </div>
                    ))}
                    <button type="button" onClick={() => handlePreviewAddTier(previewIdx)} className="am-btn am-btn--sm am-btn--add">➕ Add Tier</button>
                  </div>
                  <div className="criterion-field">
                    <label className="am-edit-label">✗ Fail:</label>
                    <textarea value={req.rubric?.fail || ''} onChange={(e) => handlePreviewRubricChange(previewIdx, 'fail', e.target.value)} className="am-edit-textarea" rows={2}/>
                  </div>
                </div>
              ))}
              <div className="rubric-editor-actions">
                <button type="button" onClick={handleAcceptAI} disabled={previewTotalPercentage !== 100} className="am-btn am-btn--success">✓ Accept & Add All Requirements</button>
                <button type="button" onClick={handleRejectAI} className="am-btn am-btn--danger">✕</button>
              </div>
            </div>
          )}
        </div>

        {(question.aiGradingCriteria || []).length > 0 && (
          <div className="existing-requirements-section">
            <div className="existing-requirements-header">
              {(() => {
                const total = (question.aiGradingCriteria || []).reduce((sum, r) => sum + (r.percentage || 0), 0);
                return (
                  <div className={`rubric-validation ${total === 100 ? 'valid' : 'invalid'}`} style={{ marginBottom: '12px' }}>
                    <span>Requirements total: {total}%{total === 100 ? ' ✓' : ' ⚠️ Must equal 100%'}</span>
                  </div>
                );
              })()}
              <h6>📋 Requirements ({question.aiGradingCriteria.length})</h6>
              <button type="button"
                onClick={() => setEditingLabRequirements(prev => ({ ...prev, [question.questionId]: !prev[question.questionId] }))}
                className="am-btn am-btn--sm am-btn--rubric-view">
                {editingLabRequirements[question.questionId] ? '🔒 Done Editing' : '✏️ Edit Requirements'}
              </button>
            </div>

            {(question.aiGradingCriteria || []).map((req, reqIdx) => (
              <div key={reqIdx} className="am-section" style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '13px' }}>Requirement {reqIdx + 1}</span>
                  {editingLabRequirements[question.questionId] && (
                    <button type="button" className="am-btn am-btn--sm am-btn--danger"
                      onClick={() => {
                        const updated = [...(question.aiGradingCriteria || [])];
                        updated.splice(reqIdx, 1);
                        handleQuestionChange(idx, qIdx, 'aiGradingCriteria', updated);
                        const totalPoints = updated.reduce((sum, r) => sum + (r.points || 0), 0);
                        handleQuestionChange(idx, qIdx, 'points', totalPoints);
                      }}>
                      🗑️ Remove
                    </button>
                  )}
                </div>

                {editingLabRequirements[question.questionId] ? (
                  <>
                    <div className="am-edit-field">
                      <label className="am-edit-label">📋 Description:</label>
                      <textarea value={req.description || ''} onChange={(e) => handleRequirementFieldChange(reqIdx, 'description', e.target.value)} className="am-edit-textarea" rows={2}/>
                    </div>
                    <div className="criterion-field">
                      <label className="am-edit-label">Weight / Percentage:</label>
                      <div className="percentage-slider-container">
                        <input type="range" min="0" max="100" value={req.percentage || 0}
                          onChange={(e) => handleRequirementFieldChange(reqIdx, 'percentage', Number(e.target.value))} className="percentage-slider"/>
                        <input type="number" min="0" max="100" value={req.percentage || 0}
                          onChange={(e) => handleRequirementFieldChange(reqIdx, 'percentage', Number(e.target.value))} className="am-edit-input" style={{ width: '70px' }}/>
                        <span className="percentage-label">%</span>
                        <span className="points-display">({req.points || 0} points)</span>
                      </div>
                    </div>
                    <div className="am-edit-field">
                      <label className="am-edit-label">✓ Full Credit:</label>
                      <textarea value={req.rubric?.full || ''} onChange={(e) => handleRubricFieldChange(reqIdx, 'full', e.target.value)} className="am-edit-textarea" rows={2}/>
                    </div>
                    <div className="am-edit-field">
                      <label className="am-edit-label">◐ Partial Tiers:</label>
                      {(req.rubric?.partial || []).map((tier, tierIdx) => (
                        <div key={tierIdx} style={{ flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--m-bg-elevated)', borderRadius: 'var(--m-radius-md)', marginBottom: '8px', border: '1px solid var(--m-border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 600, fontSize: '13px' }}>Tier {tierIdx + 1}</span>
                            <button type="button" onClick={() => {
                              const updated = [...(question.aiGradingCriteria || [])];
                              const newPartial = [...(updated[reqIdx].rubric?.partial || [])];
                              newPartial.splice(tierIdx, 1);
                              updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, partial: newPartial } };
                              handleQuestionChange(idx, qIdx, 'aiGradingCriteria', updated);
                            }} className="am-btn am-btn--sm am-btn--danger">✕ Remove</button>
                          </div>
                          <div className="criterion-field">
                            <label className="am-edit-label">Threshold: <strong>{tier.threshold || 0}%</strong></label>
                            <div className="am-q-row-left">
                              <input type="range" min="0" max="100" value={tier.threshold || 0}
                                onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'threshold', Number(e.target.value))}
                                className="percentage-slider" style={{ flex: 1 }}/>
                              <input type="number" value={tier.threshold || 0}
                                onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'threshold', Number(e.target.value))}
                                className="am-edit-input" style={{ width: '70px' }} min="0" max="100"/>
                              <span style={{ fontSize: '12px' }}>%</span>
                            </div>
                          </div>
                          <div className="criterion-field">
                            <label className="am-edit-label">Description:</label>
                            <textarea value={tier.description || ''}
                              onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'description', e.target.value)}
                              className="am-edit-textarea" rows={2}/>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={() => {
                        const updated = [...(question.aiGradingCriteria || [])];
                        const newPartial = [...(updated[reqIdx].rubric?.partial || [])];
                        newPartial.push({ threshold: 70, description: '', pointsPercentage: 0.7 });
                        updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, partial: newPartial } };
                        handleQuestionChange(idx, qIdx, 'aiGradingCriteria', updated);
                      }} className="am-btn am-btn--sm am-btn--add">➕ Add Tier</button>
                    </div>
                    <div className="am-edit-field">
                      <label className="am-edit-label">✗ Fail:</label>
                      <textarea value={req.rubric?.fail || ''} onChange={(e) => handleRubricFieldChange(reqIdx, 'fail', e.target.value)} className="am-edit-textarea" rows={2}/>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--m-text-secondary)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{req.description}</span>
                      <span className="am-points-badge">{req.points} pts ({req.percentage}%)</span>
                    </div>
                    <p style={{ margin: 0 }}><strong style={{ color: 'var(--m-green)' }}>✓ Full Credit:</strong> {req.rubric?.full}</p>
                    {(req.rubric?.partial || []).map((tier, tIdx) => (
                      <p key={tIdx} style={{ margin: 0 }}><strong style={{ color: 'var(--m-amber)' }}>◐ {tier.threshold}%:</strong> {tier.description}</p>
                    ))}
                    <p style={{ margin: 0 }}><strong style={{ color: 'var(--m-red)' }}>✗ Fail:</strong> {req.rubric?.fail}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER EDIT QUESTION CARD — from InstructorCourseManagement (Doc 4)
  // ═══════════════════════════════════════════════════════════════════════════

  const renderEditQuestionCard = (question, qIdx) => {
    const qKey = `edit_q_${question.questionId || qIdx}`;
    const isOpen = expandedSections?.[qKey] !== false;

    return (
      <div key={question.questionId || qIdx} className="am-q-row" style={{ marginBottom: '10px' }}>

        {/* ── Collapsible Header ── */}
        {/* <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
          <div className="am-q-row-left">
            <span className="am-q-row-num">Q{qIdx + 1}</span>
            <span className="am-type-badge">{formatType(question.questionType)}</span>
            <span className="am-q-row-text">
              {question.questionText?.slice(0, 60)}{question.questionText?.length > 60 ? '…' : ''}
            </span>
          </div>
          <div className="am-q-row-left">
            <span className="am-points-badge">{question.points || 0} pts</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRemoveQuestion(idx, qIdx); }}
              className="am-btn am-btn--sm am-btn--danger"
            >
              🗑️ Remove
            </button>
            <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
          </div>
        </button> */}

        <button className="am-q-row-header am-q-row-header--wrap" onClick={() => toggleSection(qKey)}>
          <div className="am-q-row-left">
            <span className="am-q-row-num">Q{qIdx + 1}</span>
            <span className="am-type-badge">{formatType(question.questionType)}</span>
            <span className="am-q-row-text">
              {question.questionText?.slice(0, 60)}{question.questionText?.length > 60 ? '…' : ''}
            </span>
          </div>
          <div className="am-q-row-right">
            <span className="am-points-badge">{question.points || 0} pts</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRemoveQuestion(idx, qIdx); }}
              className="am-btn am-btn--sm am-btn--danger"
            >
              🗑️ Remove
            </button>
            <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
          </div>
        </button>

        {/* ── Collapsible Body ── */}
        {isOpen && (
          <div className="am-q-row-body">

            <div className="am-edit-field">
              <label className="am-edit-label">🆔 Question ID:</label>
              <input type="text" value={question.questionId || ''} className="am-edit-input"
                onChange={(e) => handleQuestionChange(idx, qIdx, 'questionId', e.target.value)} />
            </div>

            {question.questionType === 'lab_practical' && (
              <div className="am-edit-field">
                <label className="am-edit-label">📌 Question Title: <span className="am-edit-req">*</span></label>
                <input type="text" value={question.questionTitle || ''} className="am-edit-input"
                  onChange={(e) => handleQuestionChange(idx, qIdx, 'questionTitle', e.target.value)}
                  placeholder="e.g., Motion Detection System" />
              </div>
            )}

            <div className="am-edit-field-row">
              <div className="am-edit-field">
                <label className="am-edit-label">🧩 Question Type:</label>
                <select value={question.questionType || 'multiple_choice'} className="am-edit-select"
                  onChange={(e) => handleQuestionTypeChange(idx, qIdx, e.target.value)}>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="true_false">True/False</option>
                  <option value="short_answer">Short Answer</option>
                  <option value="code">Code</option>
                  <option value="essay">Essay</option>
                  {assessmentType === 'lab' && <option value="lab_practical">Lab Practical</option>}
                  <option value="performance">Performance</option>
                </select>
              </div>
              <div className="am-edit-field">
                <label className="am-edit-label">
                  📈 Points:{' '}<span className="am-edit-req"> *</span>
                </label>
                <input type="number" value={question.points || ''} className="am-edit-input"
                  min="1"
                  onChange={(e) => handleQuestionChange(idx, qIdx, 'points', e.target.value)} 
                />
                {assessmentType === 'lab' && 
                  <span style={{ fontSize: '12px', color: 'var(--m-text-faint)' }}>
                    (Set total points first, then generate requirements)
                  </span>
                }
              </div>
            </div>

            <div className="am-edit-field">
              {question.questionType !== 'lab_practical'
                ? <label className="am-edit-label">✏️ Question Text: <span className="am-edit-req">*</span></label>
                : <label className="am-edit-label">🧪 Lab Objective: <span className="am-edit-req">*</span></label>}
              <textarea value={question.questionText || ''} className="am-edit-textarea" rows="4"
                onChange={(e) => handleQuestionChange(idx, qIdx, 'questionText', e.target.value)} />
            </div>

            {question.questionType === 'lab_practical' && (
              <div className="am-edit-field">
                <label className="am-edit-label">Lab Requirements: <span className="am-edit-req">*</span></label>
                <textarea value={question.labRequirements || ''} className="am-edit-textarea" rows={5}
                  onChange={(e) => handleQuestionChange(idx, qIdx, 'labRequirements', e.target.value)}
                  placeholder={'1. Code reads Serial character and prints correct output.\n2. Code is optimized with no unused parameters.\n3. No warnings or errors in circuit and code validation.'} />
              </div>
            )}

            {question.questionType === 'multiple_choice' && (
              <div className="am-edit-options-section">
                <div className="am-edit-section-header">
                  <label>Options:</label>
                  <button onClick={() => handleAddOption(idx, qIdx)} className="am-btn am-btn--sm am-btn--add">➕ Add Option</button>
                </div>
                {question.options?.map((option, oIdx) => (
                  <div key={oIdx} className="am-edit-option-row">
                    <span className="am-option-letter">{String.fromCharCode(65 + oIdx)}.</span>
                    <input type="text" value={option} className="am-edit-input-option"
                      onChange={(e) => handleOptionChange(idx, qIdx, oIdx, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + oIdx)}`} />
                    {question.options.length > 2 && (
                      <button onClick={() => handleRemoveOption(idx, qIdx, oIdx)} className="am-btn am-btn--sm am-btn--danger">✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {question.questionType !== 'lab_practical' && (
              <div className="am-edit-field">
                <label className="am-edit-label" style={{ lineHeight: 2.6 }}>✔️ Correct Answer: <span className="am-edit-req">*</span></label>
                {question.questionType === 'multiple_choice' ? (
                  <select value={question.correctAnswer || ''} className="am-edit-select"
                    onChange={(e) => handleQuestionChange(idx, qIdx, 'correctAnswer', e.target.value)}>
                    <option value="">Select correct option</option>
                    {question.options?.map((option, oIdx) => (
                      <option key={oIdx} value={option}>{String.fromCharCode(65 + oIdx)}. {option}</option>
                    ))}
                  </select>
                ) : question.questionType === 'true_false' ? (
                  <select value={question.correctAnswer || ''} className="am-edit-select"
                    onChange={(e) => handleQuestionChange(idx, qIdx, 'correctAnswer', e.target.value)}>
                    <option value="">Select answer</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : (question.questionType === 'essay' || question.questionType === 'code' || question.questionType === 'performance') ? (
                  <textarea value={question.correctAnswer || ''} className="am-edit-textarea" rows="2"
                    onChange={(e) => handleQuestionChange(idx, qIdx, 'correctAnswer', e.target.value)}
                    placeholder="Enter expected answer/rubric (optional for manual grading)" />
                ) : (
                  <textarea value={question.correctAnswer || ''} className="am-edit-textarea" rows="2"
                    onChange={(e) => handleQuestionChange(idx, qIdx, 'correctAnswer', e.target.value)}
                    placeholder="Enter the correct answer" />
                )}
              </div>
            )}

            {question.questionType === 'lab_practical' && renderLabRequirementsBuilder(question, idx, qIdx)}

            {renderRubricGenerator(question, idx, qIdx)}

            <div className="am-edit-field">
              <label className="am-edit-label">🔍 Explanation (optional):</label>
              <textarea value={question.explanation || ''} className="am-edit-textarea" rows="2"
                onChange={(e) => handleQuestionChange(idx, qIdx, 'explanation', e.target.value)}
                placeholder="Provide an explanation for the correct answer" />
            </div>

          </div>
        )}

      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER LAB VIEW — from InstructorCourseManagement (Doc 4)
  // ═══════════════════════════════════════════════════════════════════════════

  const renderLabView = (lab) => (
    <>
      {/* Lab Info */}
      <div className="am-collapsible am-collapsible--info">
        <button className="am-collapsible-header" onClick={() => toggleSection('labInfo')}>
          <span className="am-collapsible-title">📋 Lab Info</span>
          <span className="am-collapsible-chevron">{expandedSections?.labInfo ? '▲' : '▼'}</span>
        </button>
        {expandedSections?.labInfo && (
          <div className="am-collapsible-body">
            <div className="am-info-grid">
              <div className="am-badge">
                <span className="am-badge-label">Lab ID</span>
                <span className="am-badge-value" style={{ fontSize: '13px' }}>{lab.labId}</span>
              </div>
              <div className="am-badge">
                <span className="am-badge-label">Due Date</span>
                <span className="am-badge-value" style={{ fontSize: '13px' }}>{formatShortDate(lab.dueDate)}</span>
              </div>
              <div className="am-badge">
                <span className="am-badge-label">Time</span>
                <span className="am-badge-value" style={{ fontSize: '13px' }}>{new Date(lab.dueDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
              </div>
              <div className="am-badge">
                <span className="am-badge-label">Duration</span>
                <span className="am-badge-value">{lab.duration} min</span>
              </div>
              <div className="am-badge">
                <span className="am-badge-label">Grade Weight</span>
                <span className="am-badge-value">{lab.gradeWeight}</span>
              </div>
              <div className="am-badge">
                <span className="am-badge-label">Max Points</span>
                <span className="am-badge-value">{lab.maxGrade}</span>
              </div>
            </div>
            {lab.description && (
              <div className="am-section">
                <span className="am-section-title">📋 Description</span>
                <p className="am-text">{lab.description}</p>
              </div>
            )}
            <div className="am-section">
              <span className="am-section-title">🎯 Learning Objectives</span>
              <p className="am-text">{lab.instructions}</p>
            </div>
            {lab.resources && (
              <div className="am-section">
                <span className="am-section-title">📚 Resources</span>
                <p className="am-text">{lab.resources}</p>
              </div>
            )}
            {lab.resourceFileIds?.length > 0 && (
              <div className="am-section">
                <span className="am-section-title">📎 Resource Files ({lab.resourceFileIds.length})</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  {lab.resourceFileIds.map((fileId, fileIdx) => (
                    <button key={fileIdx} onClick={() => onDownload(fileId, lab.resourceFileNames?.[fileIdx] || `Lab_Resource_${fileIdx + 1}`)} className="am-btn am-btn--sm am-btn--ghost">
                      📄 {lab.resourceFileNames?.[fileIdx] || `File ${fileIdx + 1}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Experimental / Lab Questions */}
      {lab.questions?.filter(q => q.questionType === 'lab_practical').length > 0 && (
        <div className="am-collapsible am-collapsible--iot">
          <button className="am-collapsible-header" onClick={() => toggleSection('experimental')}>
            <span className="am-collapsible-title">
              🔬 Experimental Questions
              <span className="am-collapsible-count"> ({lab.questions.filter(q => q.questionType === 'lab_practical').length})</span>
            </span>
            <span className="am-collapsible-chevron">{expandedSections?.experimental ? '▲' : '▼'}</span>
          </button>
          {expandedSections?.experimental && (
            <div className="am-collapsible-body">
              
              {lab.questions.filter(q => q.questionType === 'lab_practical').map((question, qIdx) => {
                const qKey = `iot_q_${question.questionId || qIdx}`;
                const isOpen = expandedSections?.[qKey] !== false;
                return (
                  <div key={qIdx} className="am-q-row">
                    <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                      <div className="am-q-row-left">
                        <span className="am-q-row-num">Q{qIdx + 1}</span>
                        <span className="am-type-badge">{formatType(question.questionType)}</span>
                        <span className="am-q-row-text">
                          {(question.questionTitle || question.questionText)?.slice(0, 60)}
                          {(question.questionTitle || question.questionText)?.length > 60 ? '…' : ''}
                        </span>  
                      </div>
                      <div className="am-q-row-right">
                        <span className="am-points-badge">{question.points} pts</span>
                        <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="am-q-row-body">
                        {question.questionTitle && (
                          <p style={{ margin: '0 0 4px', fontWeight: 700, color: 'var(--m-text-primary)', fontSize: '14px' }}>
                            📌 {question.questionTitle}
                          </p>
                        )}
                        <span className="am-section-title">🧪 Lab Objective</span>
                        <p className="am-text">{question.questionText}</p>
                        {question.aiGradingCriteria?.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <LabGradingRubric
                              labAIGradingCriteria={question.aiGradingCriteria}
                              questionId={question.questionId}
                              isExpanded={expandedRubrics?.[question.questionId]}
                              onToggle={toggleRubric}
                              role={role}
                              onEditRubric={() => handleEditRubric(question, idx, qIdx)}
                            />
                          </div>
                        )}
                        {question.explanation && (
                          <div className="am-explanation" style={{ marginTop: '8px' }}>
                            <strong>💡 Explanation</strong>
                            <p>{question.explanation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Post-Lab Questions */}
      {lab.questions?.filter(q => q.questionType !== 'lab_practical').length > 0 && (
        <div className="am-collapsible am-collapsible--postlab">
          <button className="am-collapsible-header" onClick={() => toggleSection('postLab')}>
            <span className="am-collapsible-title">
              📝 Post-Lab Questions
              <span className="am-collapsible-count"> ({lab.questions.filter(q => q.questionType !== 'lab_practical').length})</span>
            </span>
            <span className="am-collapsible-chevron">{expandedSections?.postLab ? '▲' : '▼'}</span>
          </button>
          {expandedSections?.postLab && (
            <div className="am-collapsible-body">
              {lab.questions.filter(q => q.questionType !== 'lab_practical').map((question, qIdx) => {
                const qKey = `postlab_q_${question.questionId || qIdx}`;
                const isOpen = expandedSections?.[qKey] !== false;
                return (
                <div key={qIdx} className="am-q-row">
                  <button
                    className="am-q-row-header"
                    onClick={() => toggleSection(qKey)}
                  >
                    <div className="am-q-row-left">
                      <span className="am-q-row-num">Q{qIdx + 1}</span>
                      <span className="am-type-badge">{formatType(question.questionType)}</span>
                      <span className="am-q-row-text">{question.questionText?.slice(0, 60)}{question.questionText?.length > 60 ? '…' : ''}</span>
                    </div>
                    <div className="am-q-row-right">
                      <span className="am-points-badge">{question.points} pts</span>
                      <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="am-q-row-body">
                      <p className="am-text">{question.questionText}</p>
                      {question.questionType === 'multiple_choice' && question.options?.length > 0 && (
                        <div className="am-question-options" style={{ marginTop: '8px' }}>
                          {question.options.map((option, oIdx) => (
                            <div key={oIdx} className={`am-option-item ${option === question.correctAnswer ? 'am-option-item--correct' : ''}`}>
                              <span className="am-option-label">{String.fromCharCode(65 + oIdx)}.</span>
                              <span>{option}</span>
                              {option === question.correctAnswer && <span className="am-correct-badge">✓ Correct</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {question.questionType === 'true_false' && question.correctAnswer && (
                        <div className="am-answer-box" style={{ marginTop: '8px' }}>
                          <strong>✔️ Correct Answer:</strong> {question.correctAnswer}
                        </div>
                      )}
                      {question.questionType === 'short_answer' && question.correctAnswer && (
                        <div className="am-answer-box" style={{ marginTop: '8px', lineHeight: 1.6 }}>
                          <strong>✔️ Correct Answer:</strong>
                          <p style={{ margin: '4px 0 0' }}>{question.correctAnswer}</p>
                        </div>
                      )}
                      {question.questionType === 'code' && (
                        <div className="am-code-answer-wrap">
                          <span className="am-code-answer-label">Expected Answer:</span>
                          <pre className="am-code-answer">
                            {question.correctAnswer || 'Manual grading required'}
                          </pre>
                        </div>
                      )}
                      {question.questionType === 'essay' && (
                        <div className="am-answer-box" style={{ marginTop: '8px' }}>
                          <strong>Expected Answer/Rubric:</strong>
                          <p style={{ margin: '4px 0 0' }}>{question.correctAnswer || 'Manual grading required'}</p>
                        </div>
                      )}
                      {question.questionType === 'performance' && (
                        <div className="am-answer-box" style={{ marginTop: '8px' }}>
                          <strong>Performance Criteria:</strong>
                          <p style={{ margin: '4px 0 0' }}>{question.correctAnswer || 'Manual grading based on performance'}</p>
                        </div>
                      )}
                      {question.explanation && (
                        <div className="am-explanation" style={{ marginTop: '8px' }}>
                          <strong>💡 Explanation</strong>
                          <p>{question.explanation}</p>
                        </div>
                      )}
                      {renderRubricView(question, idx, qIdx)}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER QUIZ / EXAM VIEW — from InstructorCourseManagement (Doc 4)
  // ═══════════════════════════════════════════════════════════════════════════

  const renderQuizExamView = (it) => (
    <>
      <div className="am-info-grid" style={{ marginBottom: '12px' }}>
        <div className="am-badge">
          <span className="am-badge-label">{typeCfg.label} ID</span>
          <span className="am-badge-value" style={{ fontSize: '13px' }}>{it[typeCfg.idField]}</span>
        </div>
        <div className="am-badge">
          <span className="am-badge-label">Due Date</span>
          <span className="am-badge-value" style={{ fontSize: '13px' }}>{formatShortDate(it.dueDate)}</span>
        </div>
        <div className="am-badge">
          <span className="am-badge-label">Time</span>
          <span className="am-badge-value" style={{ fontSize: '13px' }}>{new Date(it.dueDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
        </div>
        <div className="am-badge">
          <span className="am-badge-label">Duration</span>
          <span className="am-badge-value">{it.duration} min</span>
        </div>
        <div className="am-badge">
          <span className="am-badge-label">Attempts</span>
          <span className="am-badge-value">{it.attemptsAllowed}</span>
        </div>
        <div className="am-badge">
          <span className="am-badge-label">Grade Weight</span>
          <span className="am-badge-value">{it.gradeWeight}</span>
        </div>
        <div className="am-badge">
          <span className="am-badge-label">Max Points</span>
          <span className="am-badge-value">{it.maxGrade}</span>
        </div>
      </div>

      {it.description && (
        <div className="am-section">
          <span className="am-section-title">📋 Description</span>
          <p className="am-text">{it.description}</p>
        </div>
      )}

      <div className="am-collapsible">
        <button className="am-collapsible-header" onClick={() => toggleSection('questions')}>
          <span className="am-collapsible-title">
            ❓ Questions
            <span className="am-collapsible-count"> ({typeCfg.label}) ({it.questions?.length || 0})</span>
          </span>
          <span className="am-collapsible-chevron">{expandedSections?.questions !== false ? '▲' : '▼'}</span>
        </button>
        {expandedSections?.questions !== false && (
          <div className="am-collapsible-body">
            {Array.isArray(it.questions) && it.questions.length > 0 ? (
              it.questions.map((question, qIdx) => {
                const qKey = `q_${question.questionId || qIdx}`;
                const isOpen = expandedSections?.[qKey] !== false;
                return (
                  <div key={qIdx} className="am-q-row">
                    <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                      <div className="am-q-row-left">
                        <span className="am-q-row-num">Q{qIdx + 1}</span>
                        <span className="am-type-badge">{formatType(question.questionType)}</span>
                        <span className="am-q-row-text">{question.questionText?.slice(0, 60)}{question.questionText?.length > 60 ? '…' : ''}</span>
                      </div>
                      <div className="am-q-row-right">
                        <span className="am-points-badge">{question.points} pts</span>
                        <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="am-q-row-body">
                        <p className="am-text">{question.questionText}</p>
                        <span className="am-type-badge">{formatType(question.questionType)}</span>

                        {question.questionType === 'multiple_choice' && question.options?.length > 0 && (
                          <div className="am-question-options" style={{ marginTop: '8px' }}>
                            {question.options.map((option, oIdx) => (
                              <div key={oIdx} className={`am-option-item ${option === question.correctAnswer ? 'am-option-item--correct' : ''}`}>
                                <span className="am-option-label">{String.fromCharCode(65 + oIdx)}.</span>
                                <span>{option}</span>
                                {option === question.correctAnswer && <span className="am-correct-badge">✓ Correct</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {question.questionType === 'true_false' && question.correctAnswer && (
                          <div className="am-answer-box" style={{ marginTop: '8px' }}>
                            <strong>✔️ Correct Answer:</strong> {question.correctAnswer}
                          </div>
                        )}
                        {question.questionType === 'short_answer' && question.correctAnswer && (
                          <div className="am-answer-box" style={{ marginTop: '8px', lineHeight: 1.6 }}>
                            <strong>✔️ Correct Answer:</strong>
                            <p style={{ margin: '4px 0 0' }}>{question.correctAnswer}</p>
                          </div>
                        )}
                        {question.questionType === 'code' && (
                          <div className="am-code-answer-wrap">
                            <span className="am-code-answer-label">Expected Answer:</span>
                            <pre className="am-code-answer">
                              {question.correctAnswer || 'Manual grading required'}
                            </pre>
                          </div>
                        )}
                        {question.questionType === 'essay' && (
                          <div className="am-answer-box" style={{ marginTop: '8px' }}>
                            <strong>Expected Answer:</strong>
                            <p style={{ margin: '4px 0 0' }}>{question.correctAnswer}</p>
                          </div>
                        )}
                        {question.explanation && (
                          <div className="am-explanation" style={{ marginTop: '8px' }}>
                            <strong>💡 Explanation</strong>
                            <p>{question.explanation}</p>
                          </div>
                        )}
                        {renderRubricView(question, idx, qIdx)}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="am-text">No questions available</p>
            )}
          </div>
        )}
      </div>

      {it.resources && (
        <div className="am-section">
          <span className="am-section-title">📚 Resources</span>
          <p className="am-text">{it.resources}</p>
        </div>
      )}
    </>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER LAB EDIT — from InstructorCourseManagement (Doc 4)
  // ═══════════════════════════════════════════════════════════════════════════

  const renderLabEdit = (lab) => {
    const practicalQs    = (lab.questions || []).filter(q => q.questionType === 'lab_practical');
    const postLabQs = (lab.questions || []).filter(q => q.questionType !== 'lab_practical');
    return (
      <>
        {/* ── Lab Info Section ─────────────────────────────────────── */}
        <div className="am-collapsible am-collapsible--info">
          <button className="am-collapsible-header" onClick={() => toggleSection('labInfo')}>
            <span className="am-collapsible-title">📋 Lab Info</span>
            <span className="am-collapsible-chevron">{expandedSections?.labInfo !== false ? '▲' : '▼'}</span>
          </button>
          {expandedSections?.labInfo !== false && (
            <div className="am-collapsible-body">
              <div className="am-edit-row">
                <div className="am-edit-field">
                  <label className="am-edit-label">Lab ID:</label>
                  <input type="text" value={lab.labId || ''} className="am-edit-input" readOnly
                    onChange={(e) => handleFieldChange(idx, 'labId', e.target.value)} />
                </div>
                <div className="am-edit-field">
                  <label className="am-edit-label">Title: <span className="am-edit-req">*</span></label>
                  <input type="text" value={lab.title || ''} className="am-edit-input"
                    onChange={(e) => handleFieldChange(idx, 'title', e.target.value)} />
                </div>
              </div>
              <div className="am-edit-row">
                <div className="am-edit-field">
                  <label className="am-edit-label">Due Date: <span className="am-edit-req">*</span></label>
                  <input type="datetime-local" value={formatDateTime(lab.dueDate)} className="am-edit-input"
                    onChange={(e) => handleFieldChange(idx, 'dueDate', e.target.value)} />
                </div>
                <div className="am-edit-field">
                  <label className="am-edit-label">Duration (min): <span className="am-edit-req">*</span></label>
                  <input type="text" value={lab.duration || ''} className="am-edit-input"
                    onChange={(e) => handleFieldChange(idx, 'duration', e.target.value)} />
                </div>
                <div className="am-edit-field">
                  <label className="am-edit-label">Max. Attempt: <span className="am-edit-req">*</span></label>
                  <input type="number" value={lab.attemptsAllowed || ''} className="am-edit-input"
                    onChange={(e) => handleFieldChange(idx, 'attemptsAllowed', e.target.value)} />
                </div>
              </div>
              <div className="am-edit-row">
                <div className="am-edit-field">
                  <label className="am-edit-label">Grade Weight: <span className="am-edit-req">*</span></label>
                  <input type="number" value={lab.gradeWeight || ''} className="am-edit-input"
                    onChange={(e) => handleFieldChange(idx, 'gradeWeight', e.target.value)} />
                </div>
                <div className="am-edit-field">
                  <label className="am-edit-label">🎯 Max Points <span style={{ fontSize: '11px', color: 'var(--m-text-faint)', fontWeight: 400 }}>(Auto-calculated)</span></label>
                  <input type="number" value={calculateMaxPoints(lab.questions)} readOnly
                    className="am-edit-input" style={{ backgroundColor: 'var(--m-bg-elevated)', cursor: 'not-allowed' }} />
                  <small style={{ fontSize: '11px', color: 'var(--m-text-faint)', marginTop: '2px' }}>
                    Calculated from all question points — saved on "Save Changes"
                  </small>
                </div>
              </div>
              <div className="am-edit-field">
                <label className="am-edit-label">📋 Description: <span className="am-edit-req">*</span></label>
                <textarea value={lab.description || ''} className="am-edit-textarea" rows="3"
                  onChange={(e) => handleFieldChange(idx, 'description', e.target.value)} />
              </div>
              <div className="am-edit-field">
                <label className="am-edit-label">🎯 Learning Objectives: <span className="am-edit-req">*</span></label>
                <textarea value={lab.instructions || ''} className="am-edit-textarea" rows="4"
                  onChange={(e) => handleFieldChange(idx, 'instructions', e.target.value)} />
              </div>
              <div className="am-edit-field">
                <label className="am-edit-label">📚 Resources:</label>
                <textarea value={lab.resources || ''} className="am-edit-textarea" rows="2"
                  onChange={(e) => handleFieldChange(idx, 'resources', e.target.value)} />
                {renderFileUploadSection('labs', lab.resourceFileIds, lab.resourceFileNames)}
              </div>
            </div>
          )}
        </div>

        {/* ── Experimental (Lab) Questions Section ─────────────────── */}
        <div className="am-collapsible am-collapsible--iot">
          <button className="am-collapsible-header" onClick={() => toggleSection('experimental')}>
            <span className="am-collapsible-title">
              🔬 Experimental Questions
              <span className="am-collapsible-count"> ({practicalQs.length})</span>
            </span>
            <span className="am-collapsible-chevron">{expandedSections?.experimental !== false ? '▲' : '▼'}</span>
          </button>
          {expandedSections?.experimental !== false && (
            <div className="am-collapsible-body">
              <div className="am-edit-add-question-bar" style={{ marginBottom: '12px' }}>
                <span className="am-section-title">Lab Practical Questions ({practicalQs.length})</span>
                <button
                  onClick={() => {
                    const updated = [...editedContent];
                    const existingQs = updated[0].questions || [];
                    let maxNum = 0;
                    existingQs.forEach(q => {
                      const m = q.questionId?.match(/Q(\d+)/);
                      if (m) maxNum = Math.max(maxNum, parseInt(m[1]));
                    });
                    updated[0].questions = [...existingQs, {
                      questionId: `Q${maxNum + 1}`,
                      questionTitle: '',
                      questionText: '',
                      questionType: 'lab_practical',
                      aiGradingCriteria: [],
                      labRequirements: '',
                      points: 0,
                    }];
                    setEditedContent(updated);
                    editedContentRef.current = updated; 
                  }}
                  className="am-btn am-btn--sm am-btn--add"
                >➕ Add Experimental Question</button>
              </div>
              {practicalQs.length === 0 && (
                <p className="am-text" style={{ color: 'var(--m-text-faint)', fontStyle: 'italic' }}>No experimental questions yet. Add one above.</p>
              )}
              {[...practicalQs].reverse().map((question, displayIdx) => {
                const qIdx = (lab.questions || []).lastIndexOf(question);
                return renderEditQuestionCard(question, qIdx);
              })}
            </div>
          )}
        </div>

        {/* ── Post-Lab Questions Section ────────────────────────────── */}
        <div className="am-collapsible am-collapsible--postlab">
          <button className="am-collapsible-header" onClick={() => toggleSection('postLab')}>
            <span className="am-collapsible-title">
              📝 Post-Lab Questions
              <span className="am-collapsible-count"> ({postLabQs.length})</span>
            </span>
            <span className="am-collapsible-chevron">{expandedSections?.postLab !== false ? '▲' : '▼'}</span>
          </button>
          {expandedSections?.postLab !== false && (
            <div className="am-collapsible-body">
              <div className="am-edit-add-question-bar" style={{ marginBottom: '12px' }}>
                <span className="am-section-title">Post-Lab Questions ({postLabQs.length})</span>
                <button onClick={() => handleAddQuestion(idx)} className="am-btn am-btn--sm am-btn--add">➕ Add Post-Lab Question</button>
              </div>
              {postLabQs.length === 0 && (
                <p className="am-text" style={{ color: 'var(--m-text-faint)', fontStyle: 'italic' }}>No post-lab questions yet. Add one above.</p>
              )}
              {[...postLabQs].reverse().map((question, displayIdx) => {
                const qIdx = (lab.questions || []).lastIndexOf(question);
                return renderEditQuestionCard(question, qIdx);
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER QUIZ / EXAM EDIT — from InstructorCourseManagement (Doc 4)
  // ═══════════════════════════════════════════════════════════════════════════

  const renderQuizExamEdit = (it) => {
      const idField   = typeCfg.idField;
      const typeLabel = typeCfg.label;
      const infoKey   = `${idField}_info`;
      const qKey      = `${idField}_questions`;
      return (
        <>
          {/* ── Info Section ── */}
          <div className="am-collapsible am-collapsible--info">
            <button className="am-collapsible-header" onClick={() => toggleSection(infoKey)}>
              <span className="am-collapsible-title">📋 {typeLabel} Info</span>
              <span className="am-collapsible-chevron">{expandedSections?.[infoKey] !== false ? '▲' : '▼'}</span>
            </button>
            {expandedSections?.[infoKey] !== false && (
              <div className="am-collapsible-body">
                <div className="am-edit-row">
                  <div className="am-edit-field">
                    <label className="am-edit-label">{typeLabel} ID</label>
                    <input type="text" value={it[idField] || ''} className="am-edit-input" readOnly />
                  </div>
                  <div className="am-edit-field">
                    <label className="am-edit-label">Title <span className="am-edit-req">*</span></label>
                    <input type="text" value={it.title || ''} className="am-edit-input"
                      onChange={(e) => handleFieldChange(idx, 'title', e.target.value)} />
                  </div>
                </div>
                <div className="am-edit-row">
                  <div className="am-edit-field">
                    <label className="am-edit-label">Due Date <span className="am-edit-req">*</span></label>
                    <input type="datetime-local" value={formatDateTime(it.dueDate)} className="am-edit-input"
                      onChange={(e) => handleFieldChange(idx, 'dueDate', e.target.value)} />
                  </div>
                  <div className="am-edit-field">
                    <label className="am-edit-label">Duration (min) <span className="am-edit-req">*</span></label>
                    <input type="text" value={it.duration || ''} className="am-edit-input"
                      onChange={(e) => handleFieldChange(idx, 'duration', e.target.value)} />
                  </div>
                  <div className="am-edit-field">
                    <label className="am-edit-label">Attempts Allowed</label>
                    <input type="number" value={it.attemptsAllowed || ''} className="am-edit-input"
                      onChange={(e) => handleFieldChange(idx, 'attemptsAllowed', e.target.value)} />
                  </div>
                </div>
                <div className="am-edit-row">
                  <div className="am-edit-field">
                    <label className="am-edit-label">Grade Weight <span className="am-edit-req">*</span></label>
                    <input type="number" value={it.gradeWeight || ''} className="am-edit-input"
                      onChange={(e) => handleFieldChange(idx, 'gradeWeight', e.target.value)} />
                  </div>
                  <div className="am-edit-field">
                    <label className="am-edit-label">
                      Max Points
                      <span style={{ fontSize: '10px', color: 'var(--m-text-faint)', fontWeight: 400, marginLeft: '4px' }}>Auto-calculated</span>
                    </label>
                    <input type="number" value={calculateMaxPoints(it.questions)} readOnly
                      className="am-edit-input" style={{ background: 'var(--m-bg-elevated)', cursor: 'not-allowed' }} />
                  </div>
                </div>
                <div className="am-edit-field">
                  <label className="am-edit-label">Description</label>
                  <textarea value={it.description || ''} className="am-edit-textarea" rows="3"
                    onChange={(e) => handleFieldChange(idx, 'description', e.target.value)} />
                </div>
                <div className="am-edit-field">
                  <label className="am-edit-label">Resources</label>
                  <textarea value={it.resources || ''} className="am-edit-textarea" rows="2"
                    onChange={(e) => handleFieldChange(idx, 'resources', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* ── Questions Section ── */}
          <div className="am-collapsible am-collapsible--postlab">
            <button className="am-collapsible-header" onClick={() => toggleSection(qKey)}>
              <span className="am-collapsible-title">
                ❓ Questions
                <span className="am-collapsible-count"> ({it.questions?.length || 0})</span>
              </span>
              <span className="am-collapsible-chevron">{expandedSections?.[qKey] !== false ? '▲' : '▼'}</span>
            </button>
            {expandedSections?.[qKey] !== false && (
              <div className="am-collapsible-body">
                <div className="am-edit-add-question-bar">
                  <span className="am-section-title">Questions ({it.questions?.length || 0})</span>
                  <button onClick={() => handleAddQuestion(idx)} className="am-btn am-btn--sm am-btn--add">➕ Add Question</button>
                </div>
                {[...(it.questions || [])].reverse().map((question, displayIdx) => {
                  const qIdx = (it.questions?.length || 0) - 1 - displayIdx;
                  return renderEditQuestionCard(question, qIdx);
                })}
              </div>
            )}
          </div>
        </>
      );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER GRADING PHASE — instructor sees submission + AI grades + grade form
  // ═══════════════════════════════════════════════════════════════════════════
  const renderGradingPhase = () => {
    if (!submission) return <p className="am-text">No submission selected.</p>;

    const answers     = submission?.answers || [];
    const itemContent = content;

    const labPracticalAnswers = answers.filter(a => {
      const q = itemContent?.questions?.find(q => q.questionId === a.questionId);
      return q?.questionType === 'lab_practical';
    });
    const postLabAnswers = answers.filter(a => {
      const q = itemContent?.questions?.find(q => q.questionId === a.questionId);
      return q && q.questionType !== 'lab_practical';
    });

    // When no submission, show all questions from content as "unanswered"
    const allQuestions = itemContent?.questions || [];
    const labPracticalQuestions     = !submission ? allQuestions.filter(q => q.questionType === 'lab_practical') : [];
    const postLabQuestions = !submission ? allQuestions.filter(q => q.questionType !== 'lab_practical') : [];

    const calculatedTotal = answers.reduce((sum, a) => {
      const override = gradeFormData?.answers?.[a.questionId]?.points;
      return sum + (override !== undefined ? parseFloat(override) || 0 : a.points || 0);
    }, 0);
    const maxTotal = answers.reduce((sum, a) => sum + (a.maxPoints || 0), 0);

    return (
      <div className="am-grading">

        {/* ── Student meta + total ── */}
        <div className="am-grading-info">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--m-text-primary)' }}>
                👤 {submission.studentName}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--m-text-muted)', marginTop: '2px' }}>
                {submission.loginId} · Submitted {submission.completedAt ? new Date(submission.completedAt).toLocaleString() : '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span className={`am-status-chip ${submission.status === 'graded' ? 'am-status-chip--graded' : 'am-status-chip--submitted'}`}>
                {submission.status === 'graded' ? '✅ Graded' : '⏳ Pending'}
              </span>
              <span className="am-points-badge" style={{ fontSize: '15px' }}>
                🧮 {calculatedTotal.toFixed(1)} / {maxTotal} pts
              </span>
            </div>
          </div>
        </div>

        {/* ── Experimental (Lab) Questions ── */}
        {assessmentType === 'lab' && (labPracticalAnswers.length > 0 || labPracticalQuestions.length > 0) && (
          <div className="am-collapsible am-collapsible--iot">
            <button className="am-collapsible-header" onClick={() => toggleSection('grading_experimental')}>
              <span className="am-collapsible-title">
                🔬 Experimental Questions
                <span className="am-collapsible-count"> ({labPracticalAnswers.length})</span>
              </span>
              <span className="am-collapsible-chevron">{expandedSections?.grading_experimental !== false ? '▲' : '▼'}</span>
            </button>
            {expandedSections?.grading_experimental !== false && (
              <div className="am-collapsible-body">
                {labPracticalAnswers.map((answer, aIdx) => {
                  const qKey = `grading_experimental_${answer.questionId}`;
                  const isOpen = expandedSections?.[qKey] !== false;
                  // Find matching Lab session for this question
                  const practicalLabSession = labSessions?.[answer.questionId];
                  const practicalLabKey = `${submission.loginId}_${answer.questionId}`;
                  const practicalLabModal = labGradingModals?.[practicalLabKey];
                  return (
                    <div key={answer.questionId} className="am-q-row">
                      <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                        <div className="am-q-row-left">
                          <span className="am-q-row-num">Q{aIdx + 1}</span>
                          <span className="am-type-badge">Lab Practical</span>
                          <span className="am-q-row-text">{itemContent?.questions?.find(q => q.questionId === answer.questionId)?.questionText?.slice(0, 50) || answer.questionId}</span>
                        </div>
                        <div className="am-q-row-right">
                          <span className="am-points-badge">
                            {gradeFormData?.answers?.[answer.questionId]?.points ?? answer.points ?? 0} / {answer.maxPoints} pts
                          </span>
                          <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="am-q-row-body">
                          {/* Lab simulation button — per question */}
                          {practicalLabSession && (
                            <div style={{ marginBottom: '10px' }}>
                              <button
                                className="am-btn am-btn--sm am-btn--simulation"
                                onClick={async () => {
                                  try {
                                    const circuitRes = await getCircuitBySessionId(practicalLabSession.sessionId);
                                    setLabGradingModals(prev => ({
                                      ...prev,
                                      [practicalLabKey]: { 
                                        visible: true, 
                                        simulator: practicalLabSession, 
                                        circuit: circuitRes.success ? circuitRes.data : null 
                                      }
                                    }));
                                  } catch (err) {
                                    console.error('Failed to fetch circuit:', err);
                                    setLabGradingModals(prev => ({
                                      ...prev,
                                      [practicalLabKey]: { visible: true, simulator: practicalLabSession, circuit: null }
                                    }));
                                  }
                                }}
                              >
                                🔬 View Simulation Result
                              </button>
                              {practicalLabModal?.visible && SimulationReport && ReactDOM.createPortal(
                                <SimulationReport
                                  readOnly
                                  visible={practicalLabModal.visible}
                                  simulator={practicalLabModal.simulator || practicalLabSession}
                                  circuit={practicalLabModal.circuit}
                                  onClose={() => setLabGradingModals(prev => ({
                                    ...prev,
                                    [practicalLabKey]: { ...prev[practicalLabKey], visible: false }
                                  }))}
                                />,
                                document.body
                              )}
                            </div>
                          )}
                          {renderAttemptQuestionBreakdown(answer, aIdx, itemContent)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Post-Lab / Regular Questions ── */}
        {(submission ? postLabAnswers : postLabQuestions.map(q => ({ questionId: q.questionId, points: 0, maxPoints: q.points }))).length > 0 && (
          <div className={`am-collapsible ${assessmentType === 'lab' ? 'am-collapsible--postlab' : ''}`}>
            <button className="am-collapsible-header" onClick={() => toggleSection('grading_postlab')}>
              <span className="am-collapsible-title">
                {assessmentType === 'lab' ? '📝 Post-Lab Questions' : '❓ Questions'}
                <span className="am-collapsible-count"> ({postLabAnswers.length})</span>
              </span>
              <span className="am-collapsible-chevron">{expandedSections?.grading_postlab !== false ? '▲' : '▼'}</span>
            </button>
            {expandedSections?.grading_postlab !== false && (
              <div className="am-collapsible-body">
                {(submission ? postLabAnswers : postLabQuestions.map(q => ({ questionId: q.questionId, points: 0, maxPoints: q.points }))).map((answer, aIdx) => {
                  const qKey = `grading_q_${answer.questionId}`;
                  const isOpen = expandedSections?.[qKey] !== false;
                  const originalQuestion = itemContent?.questions?.find(q => q.questionId === answer.questionId);
                  return (
                    <div key={answer.questionId} className="am-q-row">
                      <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                        <div className="am-q-row-left">
                          <span className="am-q-row-num">Q{aIdx + 1}</span>
                          <span className="am-type-badge">{formatType(originalQuestion?.questionType)}</span>
                          <span className="am-q-row-text">{originalQuestion?.questionText?.slice(0, 50) || answer.questionId}</span>
                        </div>
                        <div className="am-q-row-right">
                          <span className="am-points-badge">
                            {gradeFormData?.answers?.[answer.questionId]?.points ?? answer.points ?? 0} / {answer.maxPoints} pts
                          </span>
                          <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="am-q-row-body">
                          {renderAttemptQuestionBreakdown(answer, aIdx, itemContent)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    );
  };

  // ── renderAttemptQuestionBreakdown — from InstructorCourseManagement ─────
  const renderAttemptQuestionBreakdown = (answer, aIdx, itemContent) => {
    const originalQuestion = itemContent?.questions?.find(q => q.questionId === answer.questionId);
    if (!originalQuestion) {
      return (
        <div key={aIdx} style={{ borderLeft: '3px solid var(--m-red)', padding: '8px' }}>
          <p className="am-text">⚠️ Question data not found (ID: {answer.questionId})</p>
        </div>
      );
    }

    const overridePoints   = gradeFormData?.answers?.[answer.questionId]?.points;
    const overrideFeedback = gradeFormData?.answers?.[answer.questionId]?.feedback;
    const hasOverride      = overridePoints !== undefined || overrideFeedback !== undefined;

    return (
      <div key={aIdx}>

        {/* AI grade status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
          {answer.aiGradeMetadata?.aiGradedAt && (
            <span className={`am-status-chip ${hasOverride ? 'am-status-chip--graded' : 'am-status-chip--submitted'}`}>
              {hasOverride ? '👨‍🏫 Instructor Override' : '🤖 Prof-iX Graded'}
            </span>
          )}
          {answer.aiGradeMetadata?.aiScore != null && (
            <span className="am-points-badge" style={{ color: 'var(--m-text-faint)', textDecoration: hasOverride ? 'line-through' : 'none' }}>
              AI: {answer.aiGradeMetadata.aiScore} pts
            </span>
          )}
          <span style={{ fontWeight: 700, color: answer.isCorrect === true ? 'var(--m-green)' : answer.isCorrect === false ? 'var(--m-red)' : 'var(--m-amber)' }}>
            {answer.isCorrect === true ? '✓' : answer.isCorrect === false ? '✗' : '⏳'}
          </span>
        </div>

        {/* Question text */}
        {originalQuestion.questionText && (
          <div className="am-answer-box" style={{ marginBottom: '6px' }}>
            <strong>Question:</strong>
            <p style={{ margin: '4px 0 0' }}>{originalQuestion.questionText}</p>
          </div>
        )}

        {/* MCQ options */}
        {originalQuestion.questionType === 'multiple_choice' && originalQuestion.options && (
          <div className="am-question-options">
            {originalQuestion.options.map((option, oIdx) => (
              <div key={oIdx} className={`am-option-item ${option === answer.answer ? 'am-option-item--correct' : ''}`}>
                <span className="am-option-label">{String.fromCharCode(65 + oIdx)}.</span>
                <span>{option}</span>
                {option === answer.answer && <span className="am-correct-badge">Student's Answer</span>}
                {option === originalQuestion.correctAnswer && <span className="am-correct-badge" style={{ background: 'var(--m-green)', color: '#fff', marginLeft: '4px' }}>✔ Correct</span>}
              </div>
            ))}
          </div>
        )}

        {/* True/False — student answer + correct answer */}
        {originalQuestion.questionType === 'true_false' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '6px' }}>
            <div className="am-answer-box" style={{ borderLeft: '3px solid var(--m-role-instructor, #7c3aed)' }}>
              <strong>Student's Answer:</strong>
              <p style={{ margin: '4px 0 0' }}>
                {answer.answer ? answer.answer.charAt(0).toUpperCase() + answer.answer.slice(1) : 'No answer'}
              </p>
            </div>
            {originalQuestion.correctAnswer && (
              <div className="am-answer-box" style={{ borderLeft: '3px solid var(--m-green)' }}>
                <strong style={{ color: 'var(--m-green)' }}>✔️ Correct Answer:</strong>
                <p style={{ margin: '4px 0 0' }}>
                  {originalQuestion.correctAnswer.charAt(0).toUpperCase() + originalQuestion.correctAnswer.slice(1)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Short answer / Essay / Code — student answer + correct answer */}
        {['short_answer', 'essay', 'code'].includes(originalQuestion.questionType) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '6px' }}>
            <div className="am-answer-box" style={{ borderLeft: '3px solid var(--m-role-instructor, #7c3aed)' }}>
              <strong>Student's Answer:</strong>
              <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontFamily: originalQuestion.questionType === 'code' ? 'monospace' : 'inherit' }}>
                {answer.answer || 'No answer provided'}
              </p>
            </div>
            {originalQuestion.correctAnswer && (
              <div className="am-answer-box" style={{ borderLeft: '3px solid var(--m-green)' }}>
                <strong style={{ color: 'var(--m-green)' }}>✔️ Correct Answer:</strong>
                <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontFamily: originalQuestion.questionType === 'code' ? 'monospace' : 'inherit' }}>
                  {originalQuestion.correctAnswer}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Lab Practical — show session ID only, breakdown handled separately */}
        {originalQuestion.questionType === 'lab_practical' && (
          <div className="am-answer-box" style={{ borderLeft: '3px solid var(--m-role-instructor, #7c3aed)', marginBottom: '6px' }}>
            <strong>Lab Session:</strong>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--m-text-muted)' }}>
              {answer.sessionId || 'No session recorded'}
            </p>
          </div>
        )}

        {answer.aiGradeMetadata?.aiFeedback && (
          <div className="am-ai-grade-info" style={{ opacity: hasOverride ? 0.5 : 1 }}>
            <strong>🤖 Prof-iX Feedback{hasOverride ? ' (overridden)' : ''}:</strong>
            <p className="am-ai-feedback">{answer.aiGradeMetadata.aiFeedback}</p>
          </div>
        )}

        {/* AI Feedback + Breakdown — collapsible */}
        {(answer.aiGradeMetadata?.aiFeedback || answer.aiGradeMetadata?.breakdown) && (
          <div className="am-q-row" style={{ marginTop: '8px' }}>
            <button
              className="am-q-row-header"
              onClick={() => toggleSection(`rubric_${answer.questionId}`)}
              style={{ background: 'var(--m-bg-elevated)' }}
            >
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--m-text-secondary)' }}>
                🤖 Prof-iX Grading Rubric
              </span>
              <span className="am-btn am-btn--sm am-btn--rubric-view">
                {expandedSections?.[`rubric_${answer.questionId}`] ? '▲ Hide' : '▼ View'}
              </span>
            </button>

            {expandedSections?.[`rubric_${answer.questionId}`] && (
              <div className="am-q-row-body">
                {/* {answer.aiGradeMetadata?.aiFeedback && (
                  <div className="am-ai-grade-info" style={{ opacity: hasOverride ? 0.5 : 1 }}>
                    <strong>🤖 Prof-iX Feedback{hasOverride ? ' (overridden)' : ''}:</strong>
                    <p className="am-ai-feedback">{answer.aiGradeMetadata.aiFeedback}</p>
                  </div>
                )} */}
                {answer.aiGradeMetadata?.breakdown && (
                  <div className="am-questions-breakdown" style={{ marginTop: '6px' }}>
                    <strong>📊 Grading Breakdown:</strong>
                    <div className="am-breakdown-grid" style={{ marginTop: '6px' }}>
                      {Array.isArray(answer.aiGradeMetadata.breakdown)
                        ? answer.aiGradeMetadata.breakdown.map((item, bIdx) => (
                            <div key={bIdx} className="am-breakdown-item">
                              <div className="am-breakdown-q">{item.requirementTitle || `Requirement ${bIdx + 1}`} — {item.score}/{item.max} ({item.percentage}%)</div>
                              <div className="am-breakdown-feedback">{item.feedback}</div>
                            </div>
                          ))
                        : Object.entries(answer.aiGradeMetadata.breakdown).map(([key, criterion]) => (
                            <div key={key} className="am-breakdown-item">
                              <div className="am-breakdown-q">{key.replace(/_/g, ' ').toUpperCase()} — {criterion.score}/{criterion.max} ({criterion.percentage}%)</div>
                              <div className="am-breakdown-feedback">{criterion.feedback}</div>
                            </div>
                          ))
                      }
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Per-question override inputs */}
         {(role === 'instructor' || role === 'admin') && (
          <div style={{ display: 'flex', flexDirection:'column', gap: '10px', flexWrap: 'wrap', marginTop: '10px', padding: '10px', borderTop: '1px dashed var(--m-border-mid)', borderRadius: '12px', backgroundColor: '#f0ffff' }}>
            <label className="am-edit-label">✏️ <strong>Override By Instructor</strong></label>
            <div className="am-edit-field">
              <label className="am-edit-label">💯 Points</label>
              <div style={{display: 'flex', gap:'10px'}}>
                <input
                  type="number"
                  className="am-edit-input"
                  min="0"
                  max={answer.maxPoints}
                  value={overridePoints ?? ''}
                  onChange={(e) => onGradeFormChange?.({
                    ...gradeFormData,
                    answers: {
                      ...gradeFormData?.answers,
                      [answer.questionId]: {
                        ...gradeFormData?.answers?.[answer.questionId],
                        points: e.target.value === '' ? undefined : e.target.value
                      }
                    }
                  })}
                  placeholder={`AI: ${answer.aiGradeMetadata?.aiScore ?? answer.points ?? 0} / ${answer.maxPoints}`}
                />
                <label style={{ fontSize: '12px', fontWeight: '700', width: '100%'}}> Prof-iX Graded: {answer.aiGradeMetadata?.aiScore ?? answer.points ?? 0} / {answer.maxPoints}</label>
              </div>
            </div>
            <div className="am-edit-field" style={{ flex: 1, minWidth: '200px' }}>
              <label className="am-edit-label">💬 Feedback</label>
              <textarea
                className="am-edit-textarea"
                rows="2"
                value={overrideFeedback ?? ''}
                onChange={(e) => onGradeFormChange?.({
                  ...gradeFormData,
                  answers: {
                    ...gradeFormData?.answers,
                    [answer.questionId]: {
                      ...gradeFormData?.answers?.[answer.questionId],
                      feedback: e.target.value === '' ? undefined : e.target.value
                    }
                  }
                })}
                placeholder="Override AI feedback..."
              />
            </div>
          </div>
         )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE: student-view — instructor viewing a specific student's submissions
  // ═══════════════════════════════════════════════════════════════════════════
  const renderStudentViewPhase = () => {
    if (!content) return <p className="am-text">No content loaded.</p>;

    const idField = typeCfg.idField;
    const itemId  = content[idField];

    // Use courseProgress if available (student-view from instructor page with progress loaded)
    // Fall back to submission data directly (grading phase)
    let attempts = [];
    if (courseProgress) {
      if (assessmentType === 'lab')  attempts = courseProgress?.labProgress?.labs?.find(l => l.labId === itemId)?.attempts || [];
      if (assessmentType === 'quiz') attempts = courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId)?.attempts || [];
      if (assessmentType === 'exam') attempts = courseProgress?.examProgress?.exams?.find(e => e.examId === itemId)?.attempts || [];
    } else if (submission?.answers?.length > 0) {
      // Build a fake attempt from submission data
      attempts = [{
        attemptNumber: submission.attemptNumber || 1,
        status:        submission.status || 'submitted',
        completedAt:   submission.completedAt,
        grade:         submission.grade,
        maxGrade:      submission.maxGrade,
        answers:       submission.answers || []
      }];
    }

    // Also check for Lab sessions from submission even if no answers yet
    const submissionlabSessions = submission?.labSessions || {};
    const hasLabWork = Object.keys(submissionlabSessions).length > 0;

    return (
      <div className="am-grading">

        {/* Student meta */}
        <div className="am-grading-info">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '15px' }}>👤 {studentName || submission?.studentName}</span>
            <span style={{ fontSize: '12px', color: 'var(--m-text-muted)' }}>{studentLoginId || submission?.loginId}</span>
            <span className="am-points-badge">
              {attempts.length} / {content.attemptsAllowed || 1} attempts
            </span>
            <span className={`am-status-chip ${
              submission?.status === 'graded'    ? 'am-status-chip--graded' :
              submission?.status === 'submitted' ? 'am-status-chip--submitted' :
              submission?.answers?.length > 0 || hasLabWork ? 'am-status-chip--submitted' : ''
            }`}>
              {submission?.status === 'graded'    ? '✅ Graded' :
               submission?.status === 'submitted' ? '⏳ Pending' :
               submission?.answers?.length > 0 || hasLabWork ? '📝 In Progress' : '🔒 Not Started'}
            </span>
          </div>
        </div>

        {/* Lab work done but not yet submitted */}
        {hasLabWork && attempts.length === 0 && (
          <div className="am-section">
            <span className="am-section-title">🔬 Lab Simulation Work ({Object.keys(submissionlabSessions).length} session{Object.keys(submissionlabSessions).length > 1 ? 's' : ''})</span>
            <p style={{ fontSize: '12px', color: 'var(--m-amber)', marginBottom: '10px' }}>
              ⚠️ Student has completed Lab simulations but has not submitted the lab yet.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {Object.entries(submissionlabSessions).map(([questionId, session]) => {
                
                const practicalLabKey   = `${studentLoginId || submission?.loginId}_${questionId}`;
                const practicalLabModal = labGradingModals?.[practicalLabKey];
                const question = content.questions?.find((q) => q.questionId === questionId);
                return (
                  <div key={questionId} className="am-q-row">
                    <div className="am-q-row-header" style={{ cursor: 'default' }}>
                      <div className="am-q-row-left">
                        <span className="am-type-badge">Lab Practical</span>
                        <span className="am-q-row-text">
                          {(question?.questionTitle || question?.questionText)?.slice(0, 50) || questionId}
                        </span>
                      </div>
                      <button
                        className="am-btn am-btn--sm am-btn--simulation"
                        onClick={async () => {
                        try {
                          const circuitRes = await getCircuitBySessionId(session.sessionId);
                          setLabGradingModals(prev => ({
                            ...prev,
                            [practicalLabKey]: {
                              visible: true,
                              simulator: session,
                              circuit: circuitRes.success ? circuitRes.data : null
                            }
                          }));
                        } catch (err) {
                          setLabGradingModals(prev => ({
                            ...prev,
                            [practicalLabKey]: { visible: true, simulator: session, circuit: null }
                          }));
                        }
                      }}
                      >
                        🔬 View Lab Simulation
                      </button>
                    </div>
                    {practicalLabModal?.visible && ReactDOM.createPortal(
                      <SimulationReport
                        readOnly
                        visible={practicalLabModal.visible}
                        simulator={practicalLabModal.simulator || session}
                        circuit={practicalLabModal.circuit}
                        onClose={() => setLabGradingModals(prev => ({
                          ...prev,
                          [practicalLabKey]: { ...prev[practicalLabKey], visible: false }
                        }))}
                      />,
                      document.body
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Attempts list */}
        {attempts.length === 0 && !hasLabWork ? (
          <div className="am-section">
            <p style={{ color: 'var(--m-text-muted)', fontStyle: 'italic' }}>📭 No submissions yet</p>
          </div>
        ) : attempts.length > 0 ? (
          <div className="am-section">
            <span className="am-section-title">📊 Attempts ({attempts.length})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              {attempts.map((attempt, idx) => {
                const aKey   = `sv_attempt_${idx}`;
                const isOpen = expandedSections?.[aKey] === true;
                return (
                  <div key={idx} className="am-q-row">
                    <button className="am-q-row-header" onClick={() => toggleSection(aKey)}>
                      <div className="am-q-row-left">
                        <span className="am-q-row-num">#{idx + 1}</span>
                        <span className={`am-status-chip ${attempt.status === 'graded' ? 'am-status-chip--graded' : 'am-status-chip--submitted'}`}>
                          {attempt.status === 'graded' ? '✅ Graded' : '⏳ Pending'}
                        </span>
                        {attempt.completedAt && (
                          <span style={{ fontSize: '12px', color: 'var(--m-text-muted)' }}>
                            {new Date(attempt.completedAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="am-q-row-right">
                        {attempt.grade != null && (
                          <span className="am-points-badge">
                            {attempt.grade} / {attempt.maxGrade} pts
                          </span>
                        )}
                        <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="am-q-row-body">
                        {attempt.answers?.map((answer, aIdx) => {
                          const sessionId = answer.sessionId ||
                            submissionlabSessions[answer.questionId]?.sessionId;
                          const practicalLabKey    = `${studentLoginId || submission?.loginId}_${answer.questionId}`;
                          const practicalLabModal  = labGradingModals?.[practicalLabKey];
                          const practicalLabSession = submissionlabSessions[answer.questionId] || labSessions?.[answer.questionId];
                          return (
                            <div key={aIdx} style={{ marginBottom: '12px' }}>
                              {/* Lab simulation button */}
                              {(sessionId || practicalLabSession) && (
                                <div style={{ marginBottom: '8px' }}>
                                  <button
                                    className="am-btn am-btn--sm am-btn--simulation"
                                    onClick={async () => {
                                      try {
                                        const sim = practicalLabSession || (sessionId ? (await getSimulatorBySessionId(sessionId))?.data : null);
                                        if (sim) {
                                          const circuitRes = await getCircuitBySessionId(sim.sessionId);
                                          setLabGradingModals(prev => ({
                                            ...prev,
                                            [practicalLabKey]: {
                                              visible: true,
                                              simulator: sim,
                                              circuit: circuitRes.success ? circuitRes.data : null
                                            }
                                          }));
                                        }
                                      } catch (err) {
                                        console.error('Failed to fetch circuit:', err);
                                      }
                                    }}
                                  >
                                    🔬 View Simulation Result
                                  </button>
                                  {practicalLabModal?.visible && ReactDOM.createPortal(
                                    <SimulationReport
                                      readOnly
                                      visible={practicalLabModal.visible}
                                      simulator={practicalLabModal.simulator}
                                      circuit={practicalLabModal.circuit}
                                      onClose={() => setLabGradingModals(prev => ({
                                        ...prev,
                                        [practicalLabKey]: { ...prev[practicalLabKey], visible: false }
                                      }))}
                                    />,
                                    document.body
                                  )}
                                </div>
                              )}
                              {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE: overview — student sees content info + progress table
  // ═══════════════════════════════════════════════════════════════════════════
  const renderStudentOverview = () => {
    if (!content) return <p className="am-text">No content loaded.</p>;

    const idField = typeCfg.idField;
    const itemId  = content[idField];

    let attempts  = [];
    let bestGrade = 0;
    let avgGrade  = 0;
    let status    = 'Not Started';

    if (assessmentType === 'lab') {
      const progress = courseProgress?.labProgress?.labs?.find(l => l.labId === itemId);
      attempts  = progress?.attempts || [];
      bestGrade = progress?.bestGrade || 0;
      avgGrade  = progress?.averageGrade || 0;
    } else if (assessmentType === 'quiz') {
      const progress = courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId);
      attempts  = progress?.attempts || [];
      bestGrade = progress?.bestGrade || 0;
      avgGrade  = progress?.averageGrade || 0;
    } else if (assessmentType === 'exam') {
      const progress = courseProgress?.examProgress?.exams?.find(e => e.examId === itemId);
      attempts  = progress?.attempts || [];
      bestGrade = progress?.finalGrade || 0;
      avgGrade  = progress?.finalGrade || 0;
    }

    if (attempts.length === 0) {
      status = 'Not Started';
    } else {
      const last = attempts[attempts.length - 1];
      status = last.status === 'graded'    ? 'Graded'
             : last.status === 'submitted' ? 'Pending'
             : 'In Progress';
    }

    const attemptsUsed    = attempts.length;
    const attemptsAllowed = content.attemptsAllowed || 1;
    const exhausted       = attemptsUsed >= attemptsAllowed;

    return (
      <div className="am-grading">

        {/* ── Meta badges ── */}
        <div className="am-grading-info">
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {content.dueDate && (
              <span className="am-points-badge">📅 Due: {formatShortDate(content.dueDate)}</span>
            )}
            {content.duration && (
              <span className="am-points-badge">⏱ {content.duration} min</span>
            )}
            <span className="am-points-badge">
              Attempts: {attemptsUsed} / {attemptsAllowed}
            </span>
            {content.gradeWeight != null && (
              <span className="am-points-badge">Weight: {content.gradeWeight}</span>
            )}
            {content.maxGrade != null && (
              <span className="am-points-badge">Max: {content.maxGrade} pts</span>
            )}
            {bestGrade > 0 && (
              <span className="am-points-badge" style={{ color: 'var(--m-green)' }}>
                Best: {bestGrade.toFixed(1)} pts
              </span>
            )}
            <span className={`am-status-chip ${
              status === 'Graded'      ? 'am-status-chip--graded'    :
              status === 'Pending'     ? 'am-status-chip--submitted'  :
              status === 'In Progress' ? 'am-status-chip--submitted'  : ''
            }`}>
              {status === 'Graded'      && '✅ Graded'}
              {status === 'Pending'     && '⏳ Pending'}
              {status === 'In Progress' && '📝 In Progress'}
              {status === 'Not Started' && '🔒 Not Started'}
            </span>
          </div>
        </div>

        {/* ── Description ── */}
        {content.description && (
          <div className="am-section">
            <span className="am-section-title">📋 Description</span>
            <p className="am-text" style={{ marginTop: '6px' }}>{content.description}</p>
          </div>
        )}

        {/* ── Instructions ── */}
        {content.instructions && (
          <div className="am-section">
            <span className="am-section-title">🎯 Learning Objectives</span>
            <p className="am-text" style={{ marginTop: '6px' }}>{content.instructions}</p>
          </div>
        )}

        {/* ── Resources ── */}
        {content.resources && (
          <div className="am-section">
            <span className="am-section-title">📚 Resources</span>
            <p className="am-text" style={{ marginTop: '6px' }}>{content.resources}</p>
            {content.resourceFileIds?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {content.resourceFileIds.map((fileId, fIdx) => (
                  <button
                    key={fIdx}
                    className="am-btn am-btn--sm am-btn--ghost"
                    onClick={() => onDownload?.(fileId, content.resourceFileNames?.[fIdx] || `Resource_${fIdx + 1}`)}
                  >
                    📄 {content.resourceFileNames?.[fIdx] || `File ${fIdx + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Past attempts ── */}
        {attempts.length > 0 && (
          <div className="am-section">
            <span className="am-section-title">📊 My Attempts ({attempts.length})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              {attempts.map((attempt, idx) => {
                const aKey   = `stu_attempt_${idx}`;
                const isOpen = expandedSections?.[aKey] === true;
                return (
                  <div key={idx} className="am-q-row">
                    <button className="am-q-row-header" onClick={() => toggleSection(aKey)}>
                      <div className="am-q-row-left">
                        <span className="am-q-row-num">#{idx + 1}</span>
                        <span className={`am-status-chip ${attempt.status === 'graded' ? 'am-status-chip--graded' : 'am-status-chip--submitted'}`}>
                          {attempt.status === 'graded' ? '✅ Graded' : '⏳ Pending'}
                        </span>
                        {attempt.completedAt && (
                          <span style={{ fontSize: '12px', color: 'var(--m-text-muted)' }}>
                            {new Date(attempt.completedAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="am-q-row-right">
                        {attempt.grade != null && (
                          <span className="am-points-badge">
                            {attempt.grade} / {attempt.maxGrade} pts
                            <span style={{ marginLeft: '4px', color: 'var(--m-text-muted)' }}>
                              ({Math.round((attempt.grade / attempt.maxGrade) * 100)}%)
                            </span>
                          </span>
                        )}
                        <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="am-q-row-body">
                        {assessmentType === 'lab' ? (
                          <>
                            {/* Experimental */}
                            {attempt.answers?.filter(a => a.questionType === 'lab_practical').length > 0 && (
                              <div className="am-collapsible am-collapsible--iot">
                                <button className="am-collapsible-header" onClick={() => toggleSection(`${idx}_experimental`)}>
                                  <span className="am-collapsible-title">🔬 Experimental Questions
                                    <span className="am-collapsible-count"> ({attempt.answers.filter(a => a.questionType === 'lab_practical').length})</span>
                                  </span>
                                  <span className="am-collapsible-chevron">{expandedSections?.[`${idx}_experimental`] ? '▲' : '▼'}</span>
                                </button>
                                {expandedSections?.[`${idx}_experimental`] && (
                                  <div className="am-collapsible-body">
                                    {attempt.answers.filter(a => a.questionType === 'lab_practical').map((answer, aIdx) => {
                                      const qKey = `stu_${idx}_iot_${answer.questionId}`;
                                      const isOpen = expandedSections?.[qKey] === true;
                                      return (
                                        <div key={aIdx} className="am-q-row">
                                          <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                                            <div className="am-q-row-left">
                                              <span className="am-q-row-num">#{aIdx + 1}</span>
                                              <span className="am-type-badge">Lab Practical</span>
                                              <span className="am-q-row-text">
                                                {(() => {
                                                  const q = content.questions?.find(q => q.questionId === answer.questionId);
                                                  return (q?.questionTitle || q?.questionText)?.slice(0, 50) || answer.questionId;
                                                })()}
                                              </span>
                                            </div>
                                            <div className="am-q-row-right">
                                              <span className="am-points-badge">{answer.points ?? 0} / {answer.maxPoints} pts</span>
                                              <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                                            </div>
                                          </button>

                                          {isOpen && (
                                            <div className="am-q-row-body">
                                              {(() => {
                                                const practicalLabSession = labSessions?.[answer.questionId];
                                                const practicalLabKey = `${idx}_${answer.questionId}`;
                                                const practicalLabModal = labGradingModals?.[practicalLabKey];
                                                return practicalLabSession ? (
                                                  <div style={{ marginBottom: '10px' }}>
                                                    <button
                                                      className="am-btn am-btn--sm am-btn--simulation"
                                                    onClick={async () => {
                                                      try {
                                                        const circuitRes = await getCircuitBySessionId(practicalLabSession.sessionId);
                                                        setLabGradingModals(prev => ({
                                                          ...prev,
                                                          [practicalLabKey]: { 
                                                            visible: true, 
                                                            simulator: practicalLabSession, 
                                                            circuit: circuitRes.success ? circuitRes.data : null 
                                                          }
                                                        }));
                                                      } catch (err) {
                                                        console.error('Failed to fetch circuit:', err);
                                                        setLabGradingModals(prev => ({
                                                          ...prev,
                                                          [practicalLabKey]: { visible: true, simulator: practicalLabSession, circuit: null }
                                                        }));
                                                      }
                                                    }}
                                                    >
                                                      🔬 View Simulation Result
                                                    </button>
                                                    {practicalLabModal?.visible && ReactDOM.createPortal(
                                                      <SimulationReport
                                                        readOnly
                                                        visible={practicalLabModal.visible}
                                                        simulator={practicalLabModal.simulator || practicalLabSession}
                                                        circuit={practicalLabModal.circuit}
                                                        onClose={() => setLabGradingModals(prev => ({
                                                          ...prev,
                                                          [practicalLabKey]: { ...prev[practicalLabKey], visible: false }
                                                        }))}
                                                      />,
                                                      document.body
                                                    )}
                                                  </div>
                                                ) : null;
                                              })()}
                                              {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                                            </div>
                                          )}        

                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Post-Lab */}
                            {attempt.answers?.filter(a => a.questionType !== 'lab_practical').length > 0 && (
                              <div className="am-collapsible am-collapsible--postlab">
                                <button className="am-collapsible-header" onClick={() => toggleSection(`${idx}_postlab`)}>
                                  <span className="am-collapsible-title">📝 Post-Lab Questions
                                    <span className="am-collapsible-count"> ({attempt.answers.filter(a => a.questionType !== 'lab_practical').length})</span>
                                  </span>
                                  <span className="am-collapsible-chevron">{expandedSections?.[`${idx}_postlab`] ? '▲' : '▼'}</span>
                                </button>
                                {expandedSections?.[`${idx}_postlab`] && (
                                  <div className="am-collapsible-body">
                                    {attempt.answers.filter(a => a.questionType !== 'lab_practical').map((answer, aIdx) => {
                                      const qKey = `stu_${idx}_post_${answer.questionId}`;
                                      const isOpen = expandedSections?.[qKey] === true;
                                      return (
                                        <div key={aIdx} className="am-q-row">
                                          <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                                            <div className="am-q-row-left">
                                              <span className="am-q-row-num">Q{aIdx + 1}</span>
                                              <span className="am-type-badge">{formatType(answer.questionType)}</span>
                                              <span className="am-q-row-text">
                                                {(() => {
                                                  const q = content.questions?.find(q => q.questionId === answer.questionId);
                                                  return (q?.questionTitle || q?.questionText)?.slice(0, 50) || answer.questionId;
                                                })()}
                                              </span>
                                            </div>
                                            <div className="am-q-row-right">
                                              <span className="am-points-badge" style={{ color: answer.isCorrect ? 'var(--m-green)' : 'var(--m-red)' }}>
                                                {answer.points ?? 0} / {answer.maxPoints} pts
                                              </span>
                                              <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                                            </div>
                                          </button>
                                          {isOpen && (
                                            <div className="am-q-row-body">
                                              {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          /* Quiz / Exam — just expandable questions */
                          attempt.answers?.map((answer, aIdx) => {
                            const qKey = `stu_${idx}_q_${answer.questionId}`;
                            const isOpen = expandedSections?.[qKey] === true;
                            return (
                              <div key={aIdx} className="am-q-row">
                                <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                                  <div className="am-q-row-left">
                                    <span className="am-q-row-num">Q{aIdx + 1}</span>
                                    <span className="am-type-badge">{formatType(answer.questionType)}</span>
                                    <span className="am-q-row-text">
                                      {(() => {
                                        const q = content.questions?.find(q => q.questionId === answer.questionId);
                                        return (q?.questionTitle || q?.questionText)?.slice(0, 50) || answer.questionId;
                                      })()}
                                    </span>
                                  </div>
                                  <div className="am-q-row-right">
                                    <span className="am-points-badge" style={{ color: answer.isCorrect ? 'var(--m-green)' : 'var(--m-red)' }}>
                                      {answer.points ?? 0} / {answer.maxPoints} pts
                                    </span>
                                    <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                                  </div>
                                </button>
                                {isOpen && (
                                  <div className="am-q-row-body">
                                    {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                        {attempt.feedback && (
                          <div className="am-ai-grade-info" style={{ marginTop: '8px' }}>
                            <strong>Overall Feedback:</strong>
                            <p className="am-ai-feedback">{attempt.feedback}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE: attempt — student answering questions
  // ═══════════════════════════════════════════════════════════════════════════
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleEssayFileAdd = (questionId, files) => {
    setEssayFiles(prev => ({ ...prev, [questionId]: files }));
  };

  const handleEssayFileRemove = (questionId, fileIndex) => {
    setEssayFiles(prev => {
      const updated = Array.from(prev[questionId] || []);
      updated.splice(fileIndex, 1);
      return { ...prev, [questionId]: updated };
    });
  };

  const renderAttemptQuestion = (question, idx) => {
    const qKey        = `attempt_q_${question.questionId}`;
    const isOpen      = expandedSections?.[qKey] === true;
    const currentAnswer = answers?.[question.questionId];

    const existingCircuit = courseCircuits?.find(
      c => c.courseId === courseId &&
            c.labId === content.labId &&
            c.questionId === question.questionId &&
            c.sessionId
    );
    return (
      <div key={question.questionId} className="am-q-row">
        <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
          <div className="am-q-row-left">
            <span className="am-q-row-num">Q{idx + 1}</span>
            <span className="am-type-badge">{formatType(question.questionType)}</span>
            <span className="am-q-row-text">{(question.questionTitle || question.questionText)?.slice(0, 60)}</span>
          </div>
          <div className="am-q-row-right">
            {currentAnswer && <span style={{ color: 'var(--m-green)', fontSize: '12px' }}>✓ Answered</span>}
            <span className="am-points-badge">{question.points} pts</span>
            <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
          </div>
        </button>

        {isOpen && (
          <div className="am-q-row-body">
            <p className="am-text">
              {question.questionText}
            </p>

            {/* Lab Practical */}
            {question.questionType === 'lab_practical' && (
              <div className="am-section">
                <span className="am-section-title">📡 Lab Practical Task</span>
                <LabGradingRubric
                  labAIGradingCriteria={question.aiGradingCriteria}
                  questionId={question.questionId}
                  isExpanded={expandedRubrics[question.questionId]}
                  onToggle={toggleRubric}
                />
                {answers?.[question.questionId]?.sessionId ? (
                  <div className="am-answer-box" style={{ borderLeft: '3px solid var(--m-green)' }}>
                    <strong>✅ Practical Lab Session Active:</strong>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--m-text-muted)' }}>
                      {answers[question.questionId].sessionId}
                    </p>
                  </div>
                ) : (
                  <button
                    className="am-btn am-btn--secondary"
                    style={{color: 'white', backgroundColor: '#3658dd'}}
                    onClick={() => {
                      const encodedTitle = encodeURIComponent(question.questionTitle || question.questionText?.slice(0, 30) || question.questionId);
                      if (existingCircuit) {
                        showNotification('Opening circuit builder...', 'info');
                        // const url = `${window.location.origin}/iot/course/${courseId}/lab/${content.labId}/build?questionId=${question.questionId}&questionTitle=${encodedTitle}&circuitId=${existingCircuit.circuitId}`;
                        const url = `${window.location.origin}/labengine/course/${courseId}/lab/${content.labId}/domain/${domain}/buildcanvas?questionId=${question.questionId}&questionTitle=${encodedTitle}&circuitId=${existingCircuit.circuitId}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } else {
                        // No circuit yet — open circuit builder for new circuit
                        showNotification('Opening circuit builder for new IoT session...', 'info');
                        // const url = `${window.location.origin}/iot/course/${courseId}/lab/${content.labId}/build?questionId=${question.questionId}&questionTitle=${encodedTitle}`;
                        const url = `${window.location.origin}/labengine/course/${courseId}/lab/${content.labId}/domain/${domain}/buildcanvas?questionId=${question.questionId}&questionTitle=${encodedTitle}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {existingCircuit ? '🔧 Open Practical Lab Circuit' : '🚀 Start Practical Lab Session'}
                  </button>
                )}
                <div className="am-edit-field" style={{ marginTop: '10px' }}>
                  <label className="am-edit-label">Observations/Notes (optional):</label>
                  <textarea
                    className="am-edit-textarea"
                    rows="3"
                    value={answers?.[question.questionId]?.notes || ''}
                    onChange={(e) => handleAnswerChange(question.questionId, {
                      ...answers?.[question.questionId],
                      notes: e.target.value
                    })}
                    placeholder="Write your observations..."
                  />
                </div>
              </div>
            )}

            {/* Multiple choice */}
            {question.questionType === 'multiple_choice' && (
              <div className="am-question-options">
                {question.options?.map((option, oIdx) => (
                  <label key={oIdx}
                    className={`am-option-item ${currentAnswer === option ? 'am-option-item--correct' : ''}`}
                    style={{ cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name={`q-${question.questionId}`}
                      value={option}
                      checked={currentAnswer === option}
                      onChange={() => handleAnswerChange(question.questionId, option)}
                      style={{ marginRight: '8px' }}
                    />
                    <span className="am-option-label">{String.fromCharCode(65 + oIdx)}.</span>
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            )}

            {/* True/False */}
            {question.questionType === 'true_false' && (
              <div className="am-question-options">
                {['true', 'false'].map((val) => (
                  <label key={val}
                    className={`am-option-item ${currentAnswer === val ? 'am-option-item--correct' : ''}`}
                    style={{ cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name={`q-${question.questionId}`}
                      value={val}
                      checked={currentAnswer === val}
                      onChange={() => handleAnswerChange(question.questionId, val)}
                      style={{ marginRight: '8px' }}
                    />
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </label>
                ))}
              </div>
            )}

            {/* Short answer / Essay / Code */}
            {(question.questionType === 'short_answer' || question.questionType === 'essay' || question.questionType === 'code') && (
              <div className="am-edit-field">
                <textarea
                  className="am-edit-textarea"
                  rows={question.questionType === 'essay' ? 10 : question.questionType === 'code' ? 15 : 4}
                  value={currentAnswer || ''}
                  onChange={(e) => handleAnswerChange(question.questionId, e.target.value)}
                  placeholder={
                    question.questionType === 'essay' ? 'Write your essay answer here...' :
                    question.questionType === 'code'  ? 'Paste your code here...' :
                    'Type your answer here...'
                  }
                  style={question.questionType === 'code' ? {
                    fontFamily: 'monospace', fontSize: '13px', background: '#1e1e2e', color: '#a6e3a1'
                  } : {}}
                />
                <div style={{ fontSize: '11px', color: 'var(--m-text-muted)', marginTop: '4px' }}>
                  {(currentAnswer || '').length} characters
                </div>

                {question.questionType === 'essay' && (
                  <div style={{ marginTop: '10px' }}>
                    <label className="am-edit-label">📎 Attach File (optional)</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      multiple
                      onChange={(e) => handleEssayFileAdd(question.questionId, e.target.files)}
                      style={{ display: 'block', marginTop: '6px' }}
                    />
                    {essayFiles?.[question.questionId]?.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {Array.from(essayFiles[question.questionId]).map((file, fIdx) => (
                          <div key={fIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                            <span>📄 {file.name}</span>
                            <span style={{ color: 'var(--m-text-muted)' }}>({(file.size / 1024).toFixed(1)} KB)</span>
                            <button className="am-btn am-btn--sm am-btn--ghost"
                              onClick={() => handleEssayFileRemove(question.questionId, fIdx)}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {renderRubricView(question)}
          </div>
        )}
      </div>
    );
  };

  const renderAttemptPhase = () => {
    if (!content) return <p className="am-text">No content loaded.</p>;
    const questions = content.questions || [];

    return (
      <div className="am-grading">

        {/* Upload progress banner */}
        {uploadingFiles && (
          <div className="am-grading-info" style={{ background: 'var(--m-blue-faint)', borderLeft: '3px solid var(--m-blue)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>⏳</span>
              <div>
                <strong>Uploading files...</strong>
                {uploadProgress && Object.entries(uploadProgress).map(([qId, status]) => (
                  <p key={qId} style={{ margin: '2px 0', fontSize: '12px' }}>Question {qId}: {status}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Meta info */}
        <div className="am-grading-info">
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {content.duration && <span className="am-points-badge">⏱ {content.duration} min</span>}
            {content.maxGrade && <span className="am-points-badge">📈 {content.maxGrade} pts total</span>}
          </div>
        </div>

        {/* Timer for quiz/exam */}
        {(assessmentType === 'quiz' || assessmentType === 'exam') && timeRemaining !== null && (
          <div className="am-grading-info" style={{ 
            background: timeRemaining < 60 ? 'var(--m-red-dim)' : timeRemaining < 300 ? 'var(--m-amber-dim)' : 'var(--m-bg-elevated)',
            borderLeft: `3px solid ${timeRemaining < 60 ? 'var(--m-red)' : timeRemaining < 300 ? 'var(--m-amber)' : 'var(--m-border)'}`
          }}>
            <div className="am-q-row-left">
              <span style={{ fontSize: '18px' }}>⏱</span>
              <span style={{ 
                fontWeight: 700, 
                fontSize: '16px',
                color: timeRemaining < 60 ? 'var(--m-red)' : timeRemaining < 300 ? 'var(--m-amber)' : 'var(--m-text-primary)'
              }}>
                {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
              </span>
              <span style={{ fontSize: '13px', color: 'var(--m-text-muted)' }}>remaining</span>
              {timeRemaining < 300 && (
                <span style={{ fontSize: '12px', color: timeRemaining < 60 ? 'var(--m-red)' : 'var(--m-amber)', fontWeight: 600 }}>
                  {timeRemaining < 60 ? '⚠️ Less than 1 minute!' : '⚠️ Less than 5 minutes!'}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Questions */}
        {assessmentType === 'lab' ? (
          <>
            {/* Experimental Questions */}
            {questions.filter(q => q.questionType === 'lab_practical').length > 0 && (
              <div className="am-collapsible am-collapsible--iot">
                <button className="am-collapsible-header" onClick={() => toggleSection('attempt_experimental')}>
                  <span className="am-collapsible-title">
                    🔬 Experimental Questions
                    <span className="am-collapsible-count"> ({questions.filter(q => q.questionType === 'lab_practical').length})</span>
                  </span>
                  <span className="am-collapsible-chevron">{expandedSections?.attempt_experimental !== false ? '▲' : '▼'}</span>
                </button>
                {expandedSections?.attempt_experimental !== false && (
                  <div className="am-collapsible-body">
                    {questions.filter(q => q.questionType === 'lab_practical').map((question, idx) => (
                      renderAttemptQuestion(question, idx)
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Post-Lab Questions */}
            {questions.filter(q => q.questionType !== 'lab_practical').length > 0 && (
              <div className="am-collapsible am-collapsible--postlab">
                <button className="am-collapsible-header" onClick={() => toggleSection('attempt_postlab')}>
                  <span className="am-collapsible-title">
                    📝 Post-Lab Questions
                    <span className="am-collapsible-count"> ({questions.filter(q => q.questionType !== 'lab_practical').length})</span>
                  </span>
                  <span className="am-collapsible-chevron">{expandedSections?.attempt_postlab !== false ? '▲' : '▼'}</span>
                </button>
                {expandedSections?.attempt_postlab !== false && (
                  <div className="am-collapsible-body">
                    {questions.filter(q => q.questionType !== 'lab_practical').map((question, idx) => (
                      renderAttemptQuestion(question, idx)
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* Quiz/Exam — flat list */
          questions.map((question, idx) => renderAttemptQuestion(question, idx))
        )}

      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE: results — student sees submission results
  // ═══════════════════════════════════════════════════════════════════════════
  const renderResultsPhase = () => {
    if (!content) return <p className="am-text">No content loaded.</p>;

    const idField = typeCfg.idField;
    const itemId  = content[idField];

    let attempts  = [];
    let bestGrade = 0;
    let avgGrade  = 0;
    let status    = 'Not Started';

    if (assessmentType === 'lab') {
      const progress = courseProgress?.labProgress?.labs?.find(l => l.labId === itemId);
      attempts  = progress?.attempts || [];
      bestGrade = progress?.bestGrade || 0;
      avgGrade  = progress?.averageGrade || 0;
    } else if (assessmentType === 'quiz') {
      const progress = courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId);
      attempts  = progress?.attempts || [];
      bestGrade = progress?.bestGrade || 0;
      avgGrade  = progress?.averageGrade || 0;
    } else if (assessmentType === 'exam') {
      const progress = courseProgress?.examProgress?.exams?.find(e => e.examId === itemId);
      attempts  = progress?.attempts || [];
      bestGrade = progress?.finalGrade || 0;
      avgGrade  = progress?.finalGrade || 0;
    }

    if (attempts.length === 0) {
      status = 'Not Started';
    } else {
      const last = attempts[attempts.length - 1];
      status = last.status === 'graded'    ? 'Graded'
             : last.status === 'submitted' ? 'Pending'
             : 'In Progress';
    }

    const attemptsUsed    = attempts.length;
    const attemptsAllowed = content.attemptsAllowed || 1;
    const exhausted       = attemptsUsed >= attemptsAllowed;
    
    return (
      <div className="am-grading">
        {isGrading ? (
          <div className="am-grading-pending-state">
            <div className="am-grading-pending-spinner" />
            <div className="am-grading-pending-title">
              🤖 Prof-iX is grading your submission…
            </div>
            <p className="am-grading-pending-subtitle">
              This usually takes a few seconds. Please wait.
            </p>
          </div>
          ): (
          <>
            {/* ── Meta badges ── */}
            <div className="am-grading-info">
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {content.dueDate && (
                  <span className="am-points-badge">📅 Due: {formatShortDate(content.dueDate)}</span>
                )}
                {content.duration && (
                  <span className="am-points-badge">⏱ {content.duration} min</span>
                )}
                <span className="am-points-badge">
                  Attempts: {attemptsUsed} / {attemptsAllowed}
                </span>
                {content.gradeWeight != null && (
                  <span className="am-points-badge">Weight: {content.gradeWeight}</span>
                )}
                {content.maxGrade != null && (
                  <span className="am-points-badge">Max: {content.maxGrade} pts</span>
                )}
                {bestGrade > 0 && (
                  <span className="am-points-badge" style={{ color: 'var(--m-green)' }}>
                    Best: {bestGrade.toFixed(1)} pts
                  </span>
                )}
                <span className={`am-status-chip ${
                  status === 'Graded'      ? 'am-status-chip--graded'    :
                  status === 'Pending'     ? 'am-status-chip--submitted'  :
                  status === 'In Progress' ? 'am-status-chip--submitted'  : ''
                }`}>
                  {status === 'Graded'      && '✅ Graded'}
                  {status === 'Pending'     && '⏳ Pending'}
                  {status === 'In Progress' && '📝 In Progress'}
                  {status === 'Not Started' && '🔒 Not Started'}
                </span>
              </div>
            </div>

            {/* ── Description ── */}
            {content.description && (
              <div className="am-section">
                <span className="am-section-title">📋 Description</span>
                <p className="am-text" style={{ marginTop: '6px' }}>{content.description}</p>
              </div>
            )}

            {/* ── Instructions ── */}
            {content.instructions && (
              <div className="am-section">
                <span className="am-section-title">🎯 Learning Objectives</span>
                <p className="am-text" style={{ marginTop: '6px' }}>{content.instructions}</p>
              </div>
            )}

            {/* ── Resources ── */}
            {content.resources && (
              <div className="am-section">
                <span className="am-section-title">📚 Resources</span>
                <p className="am-text" style={{ marginTop: '6px' }}>{content.resources}</p>
                {content.resourceFileIds?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {content.resourceFileIds.map((fileId, fIdx) => (
                      <button
                        key={fIdx}
                        className="am-btn am-btn--sm am-btn--ghost"
                        onClick={() => onDownload?.(fileId, content.resourceFileNames?.[fIdx] || `Resource_${fIdx + 1}`)}
                      >
                        📄 {content.resourceFileNames?.[fIdx] || `File ${fIdx + 1}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Past attempts ── */}
            {attempts.length > 0 && (
              <div className="am-section">
                <span className="am-section-title">📊 My Attempts ({attempts.length})</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
                  {attempts.map((attempt, idx) => {
                    const aKey   = `stu_attempt_${idx}`;
                    const isOpen = expandedSections?.[aKey] === true;
                    return (
                      <div key={idx} className="am-q-row">
                        <button className="am-q-row-header" onClick={() => toggleSection(aKey)}>
                          <div className="am-q-row-left">
                            <span className="am-q-row-num">#{idx + 1}</span>
                            <span className={`am-status-chip ${attempt.status === 'graded' ? 'am-status-chip--graded' : 'am-status-chip--submitted'}`}>
                              {attempt.status === 'graded' ? '✅ Graded' : '⏳ Pending'}
                            </span>
                            {attempt.completedAt && (
                              <span style={{ fontSize: '12px', color: 'var(--m-text-muted)' }}>
                                {new Date(attempt.completedAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="am-q-row-right">
                            {attempt.grade != null && (
                              <span className="am-points-badge">
                                {attempt.grade} / {attempt.maxGrade} pts
                                <span style={{ marginLeft: '4px', color: 'var(--m-text-muted)' }}>
                                  ({Math.round((attempt.grade / attempt.maxGrade) * 100)}%)
                                </span>
                              </span>
                            )}
                            <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                          </div>
                        </button>

                        {isOpen && (
                          <div className="am-q-row-body">
                            {assessmentType === 'lab' ? (
                              <>
                                {/* Experimental */}
                                {attempt.answers?.filter(a => a.questionType === 'lab_practical').length > 0 && (
                                  <div className="am-collapsible am-collapsible--iot">
                                    <button className="am-collapsible-header" onClick={() => toggleSection(`${idx}_experimental`)}>
                                      <span className="am-collapsible-title">🔬 Experimental Questions
                                        <span className="am-collapsible-count"> ({attempt.answers.filter(a => a.questionType === 'lab_practical').length})</span>
                                      </span>
                                      <span className="am-collapsible-chevron">{expandedSections?.[`${idx}_experimental`] ? '▲' : '▼'}</span>
                                    </button>
                                    {expandedSections?.[`${idx}_experimental`] && (
                                      <div className="am-collapsible-body">
                                        {attempt.answers.filter(a => a.questionType === 'lab_practical').map((answer, aIdx) => {
                                          const qKey = `stu_${idx}_iot_${answer.questionId}`;
                                          const isOpen = expandedSections?.[qKey] === true;
                                          return (
                                            <div key={aIdx} className="am-q-row">
                                              <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                                                <div className="am-q-row-left">
                                                  <span className="am-q-row-num">Q{aIdx + 1}</span>
                                                  <span className="am-type-badge">Lab Practical</span>
                                                </div>
                                                <div className="am-q-row-right">
                                                  <span className="am-points-badge">{answer.points ?? 0} / {answer.maxPoints} pts</span>
                                                  <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                                                </div>
                                              </button>

                                              {isOpen && (
                                                <div className="am-q-row-body">
                                                  {(() => {
                                                    const practicalLabSession = labSessions?.[answer.questionId];
                                                    const practicalLabKey = `${idx}_${answer.questionId}`;
                                                    const practicalLabModal = labGradingModals?.[practicalLabKey];
                                                    return practicalLabSession ? (
                                                      <div style={{ marginBottom: '10px' }}>
                                                        <button
                                                          className="am-btn am-btn--sm am-btn--simulation"
                                                          onClick={async () => {
                                                            try {
                                                              const circuitRes = await getCircuitBySessionId(practicalLabSession.sessionId);
                                                              setLabGradingModals(prev => ({
                                                                ...prev,
                                                                [practicalLabKey]: { 
                                                                  visible: true, 
                                                                  simulator: practicalLabSession, 
                                                                  circuit: circuitRes.success ? circuitRes.data : null 
                                                                }
                                                              }));
                                                            } catch (err) {
                                                              console.error('Failed to fetch circuit:', err);
                                                              setLabGradingModals(prev => ({
                                                                ...prev,
                                                                [practicalLabKey]: { visible: true, simulator: practicalLabSession, circuit: null }
                                                              }));
                                                            }
                                                          }}
                                                        >
                                                          🔬 View Simulation Result
                                                        </button>
                                                        {practicalLabModal?.visible && ReactDOM.createPortal(
                                                          <SimulationReport
                                                            readOnly
                                                            visible={practicalLabModal.visible}
                                                            simulator={practicalLabModal.simulator || practicalLabSession}
                                                            circuit={practicalLabModal.circuit}
                                                            onClose={() => setLabGradingModals(prev => ({
                                                              ...prev,
                                                              [practicalLabKey]: { ...prev[practicalLabKey], visible: false }
                                                            }))}
                                                          />,
                                                          document.body
                                                        )}
                                                      </div>
                                                    ) : null;
                                                  })()}
                                                  {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                                                </div>
                                              )}        

                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Post-Lab */}
                                {attempt.answers?.filter(a => a.questionType !== 'lab_practical').length > 0 && (
                                  <div className="am-collapsible am-collapsible--postlab">
                                    <button className="am-collapsible-header" onClick={() => toggleSection(`${idx}_postlab`)}>
                                      <span className="am-collapsible-title">📝 Post-Lab Questions
                                        <span className="am-collapsible-count"> ({attempt.answers.filter(a => a.questionType !== 'lab_practical').length})</span>
                                      </span>
                                      <span className="am-collapsible-chevron">{expandedSections?.[`${idx}_postlab`] ? '▲' : '▼'}</span>
                                    </button>
                                    {expandedSections?.[`${idx}_postlab`] && (
                                      <div className="am-collapsible-body">
                                        {attempt.answers.filter(a => a.questionType !== 'lab_practical').map((answer, aIdx) => {
                                          const qKey = `stu_${idx}_post_${answer.questionId}`;
                                          const isOpen = expandedSections?.[qKey] === true;
                                          return (
                                            <div key={aIdx} className="am-q-row">
                                              <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                                                <div className="am-q-row-left">
                                                  <span className="am-q-row-num">Q{aIdx + 1}</span>
                                                  <span className="am-type-badge">{formatType(answer.questionType)}</span>
                                                </div>
                                                <div className="am-q-row-right">
                                                  <span className="am-points-badge" style={{ color: answer.isCorrect ? 'var(--m-green)' : 'var(--m-red)' }}>
                                                    {answer.points ?? 0} / {answer.maxPoints} pts
                                                  </span>
                                                  <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                                                </div>
                                              </button>
                                              {isOpen && (
                                                <div className="am-q-row-body">
                                                  {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              /* Quiz / Exam — just expandable questions */
                              attempt.answers?.map((answer, aIdx) => {
                                const qKey = `stu_${idx}_q_${answer.questionId}`;
                                const isOpen = expandedSections?.[qKey] === true;
                                return (
                                  <div key={aIdx} className="am-q-row">
                                    <button className="am-q-row-header" onClick={() => toggleSection(qKey)}>
                                      <div className="am-q-row-left">
                                        <span className="am-q-row-num">Q{aIdx + 1}</span>
                                        <span className="am-type-badge">{formatType(answer.questionType)}</span>
                                      </div>
                                      <div className="am-q-row-right">
                                        <span className="am-points-badge" style={{ color: answer.isCorrect ? 'var(--m-green)' : 'var(--m-red)' }}>
                                          {answer.points ?? 0} / {answer.maxPoints} pts
                                        </span>
                                        <span className="am-collapsible-chevron">{isOpen ? '▲' : '▼'}</span>
                                      </div>
                                    </button>
                                    {isOpen && (
                                      <div className="am-q-row-body">
                                        {renderAttemptQuestionBreakdown(answer, aIdx, content)}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                            {attempt.feedback && (
                              <div className="am-ai-grade-info" style={{ marginTop: '8px' }}>
                                <strong>Overall Feedback:</strong>
                                <p className="am-ai-feedback">{attempt.feedback}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // JSX — role & phase aware shell
  // ═══════════════════════════════════════════════════════════════════════════

  const roleLabel = role === 'instructor' ? 'Instructor' : role === 'admin' ? 'Admin' : 'Student';

  return (
    <div className="am-overlay" onClick={() => {
      if (submitConfirmAlert) return;
      onClose();
    }}>
      <div className={`am-shell am--${role} am--${assessmentType}`} onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <div className="am-header">
          <div className="am-hdr-icon">{typeCfg.icon}</div>
          <div className="am-hdr-text">
            <div className="am-hdr-title">
              {phase === 'grading'            ? `Grading: ${submission?.studentName || 'Student'}`
               : phase === 'student-view'     ? `${studentName}'s ${typeCfg.label}`
               : assessmentPhase === 'attempt'  ? `📝 ${content?.title || typeCfg.label}`
               : assessmentPhase === 'results'  ? `📊 Results: ${content?.title || typeCfg.label}`
               : (displayItem?.title || typeCfg.label)}
              {editing && <span className="am-edit-pill">✏️ Editing</span>}
            </div>

            <div className="am-hdr-sub">
              {phase === 'grading'              ? `${typeCfg.label} · ${submission?.[typeCfg.idField] || ''}`
               : phase === 'student-view'       ? `${studentLoginId} · ${content?.[typeCfg.idField] || ''}`
               : assessmentPhase === 'attempt'  ? `${content?.[typeCfg.idField] || ''} · ${content?.duration ? content.duration + ' min' : ''}`
               : assessmentPhase === 'results'  ? `${content?.[typeCfg.idField] || ''}`
               : assessmentPhase === 'overview' && role === 'student' ? `${content?.[typeCfg.idField] || ''}`
               : displayItem?.[typeCfg.idField]}
            </div>
          </div>
          <div className="am-hdr-chips">
            <span className="am-type-chip">{typeCfg.label}</span>
            <span className="am-role-chip">{roleLabel}</span>
            {phase === 'grading'      && <span className="am-status-chip am-status-chip--submitted">Grading</span>}
            {phase === 'student-view' && <span className="am-status-chip am-status-chip--submitted">Viewing</span>}
            {assessmentPhase === 'overview' && role === 'student' && (() => {
              const idField = typeCfg.idField;
              const itemId  = content?.[idField];
              let attempts  = [];
              if (assessmentType === 'lab')  attempts = courseProgress?.labProgress?.labs?.find(l => l.labId === itemId)?.attempts || [];
              if (assessmentType === 'quiz') attempts = courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId)?.attempts || [];
              if (assessmentType === 'exam') attempts = courseProgress?.examProgress?.exams?.find(e => e.examId === itemId)?.attempts || [];
              const hasSubmitted = attempts.length > 0;
              return hasSubmitted
                ? <span className="am-status-chip am-status-chip--graded">Results</span>
                : <span className="am-status-chip am-status-chip--submitted">Overview</span>;
            })()}
            {assessmentPhase === 'attempt'  && <span className="am-status-chip am-status-chip--submitted">In Progress</span>}
            {assessmentPhase === 'results'  && <span className="am-status-chip am-status-chip--graded">Results</span>}  
          </div>
          {/* <div className="am-hdr-actions">
            {phase === 'overview' && (role === 'instructor' || role === 'admin') && (
              editing ? (
                <>
                  <button className="am-btn am-btn--sm am-btn--ghost"   onClick={handleCancelEdit}>Cancel</button>
                  <button className="am-btn am-btn--sm am-btn--success" onClick={handleSaveEdit}>✓ Save</button>
                </>
              ) : (
                <>
                  <button className="am-btn am-btn--sm am-btn--ghost"     onClick={generatePDF}       title="Download as PDF">⬇️ PDF</button>
                  <button className="am-btn am-btn--sm am-btn--secondary" onClick={handleEditContent}>✏️ Edit</button>
                </>
              )
            )}
            <button className="am-close" onClick={onClose}>✕</button>
          </div> */}

          <div className="am-hdr-actions">

            {/* ── phase: grading ── */}
            {phase === 'grading' && (() => {
              const hasSubmitted = submission?.status === 'submitted' || submission?.status === 'graded';
              const hasAnswers   = submission?.answers?.length > 0;
              const hasLabWork   = submission?.labSessions && Object.keys(submission.labSessions).length > 0;
              const hasCircuits  = allStudentCircuits?.some(c =>
                c.courseId === courseId &&
                c.labId === submission?.labId &&
                (c.studentId === submission?.loginId || c.studentId === submission?.studentLoginId)
              );
              const isInProgress = !hasSubmitted && (hasAnswers || hasLabWork || hasCircuits);
              const notStarted   = !hasSubmitted && !hasAnswers && !hasLabWork && !hasCircuits;
              return (
                <>
                  {hasSubmitted  && <span className="am-status-label am-status-label--green">✅ Submitted</span>}
                  {isInProgress  && <span className="am-status-label am-status-label--amber">📝 In Progress</span>}
                  {notStarted    && <span className="am-status-label am-status-label--muted">🔒 Not Started</span>}
                  {(hasSubmitted || isInProgress) && (
                    <button className="am-btn am-btn--sm am-btn--secondary" onClick={() => setAssessmentPhase('student-view')}>
                      👁️ View
                    </button>
                  )}
                  {hasSubmitted ? (
                    <button className="am-btn am-btn--sm am-btn--secondary" onClick={() => onSubmitGrade?.(submission, gradeFormData)}>
                      ✏️ Override
                    </button>
                  ) : (
                    <button className="am-btn am-btn--sm am-btn--primary" onClick={() => onSubmitGrade?.(submission, gradeFormData)}>
                      💾 Grade
                    </button>
                  )}
                  <button className="am-btn am-btn--sm am-btn--ghost" onClick={onClose}>Close</button>
                </>
              );
            })()}

            {/* ── phase: student-view ── */}
            {(phase === 'student-view' || assessmentPhase === 'student-view') && (
              <>
                {assessmentPhase === 'student-view' && phase === 'grading' && (
                  <button className="am-btn am-btn--sm am-btn--ghost" onClick={() => setAssessmentPhase('grading')}>← Back</button>
                )}
                <button className="am-btn am-btn--sm am-btn--ghost" onClick={onClose}>Close</button>
              </>
            )}

            {/* ── phase: attempt ── */}
            {assessmentPhase === 'attempt' && (
              <>
                <button className="am-btn am-btn--sm am-btn--ghost" onClick={onClose}>Cancel</button>
                <button
                  className="am-btn am-btn--sm am-btn--primary"
                  disabled={isSubmitting || uploadingFiles}
                  onClick={handleSubmitAttempt}
                >
                  {uploadingFiles ? '⏫ Uploading...' : isSubmitting ? '⏳...' : `📤 Submit`}
                </button>
              </>
            )}

            {/* ── phase: results ── */}
            {assessmentPhase === 'results' && (
              <button className="am-btn am-btn--sm am-btn--primary" disabled={isGrading} onClick={() => {
                if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
                onClose();
              }}>
                {isGrading ? '⏳ Grading...' : '✓ Done'}
              </button>
            )}

            {/* ── phase: student overview ── */}
            {assessmentPhase === 'overview' && role === 'student' && phase !== 'grading' && (() => {
              const idField       = typeCfg.idField;
              const itemId        = content?.[idField];
              const attemptsUsed  = assessmentType === 'lab'  ? courseProgress?.labProgress?.labs?.find(l => l.labId === itemId)?.attemptsUsed || 0
                                  : assessmentType === 'quiz' ? courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId)?.attemptsUsed || 0
                                  : assessmentType === 'exam' ? courseProgress?.examProgress?.exams?.find(e => e.examId === itemId)?.attemptsUsed || 0
                                  : 0;
              const attemptsAllowed = content?.attemptsAllowed || 1;
              const exhausted       = attemptsUsed >= attemptsAllowed;
              return exhausted ? (
                <span className="am-status-label am-status-label--red">⚠️ {attemptsUsed}/{attemptsAllowed} used</span>
              ) : (
                <button className="am-btn am-btn--sm am-btn--primary" onClick={() => {
                  setAnswers({}); setEssayFiles({}); setSubmissionResults(null); setAssessmentPhase('attempt');
                }}>
                  ▶️ Start
                </button>
              );
            })()}

            {/* ── phase: instructor/admin overview — edit controls (already existed) ── */}
            {phase === 'overview' && (role === 'instructor' || role === 'admin') && (
              editing ? (
                <>
                  <button className="am-btn am-btn--sm am-btn--ghost"   onClick={handleCancelEdit}>Cancel</button>
                  <button className="am-btn am-btn--sm am-btn--success" onClick={handleSaveEdit}>✓ Save</button>
                </>
              ) : (
                <>
                  <button className="am-btn am-btn--sm am-btn--ghost"     onClick={generatePDF}       title="Download as PDF">⬇️ PDF</button>
                  <button className="am-btn am-btn--sm am-btn--secondary" onClick={handleEditContent}>✏️ Edit</button>
                </>
              )
            )}

            {/* ── Close is always last ── */}
            {phase !== 'grading' && assessmentPhase !== 'attempt' && assessmentPhase !== 'results' && 
            !(phase === 'student-view' || assessmentPhase === 'student-view') &&
            !(assessmentPhase === 'overview' && role === 'student') && (
              <button className="am-close" onClick={onClose}>✕</button>
            )}
            {/* For phases that already have a close/cancel above, just show ✕ */}
            {(assessmentPhase === 'attempt' || assessmentPhase === 'results' ||
              phase === 'grading' || phase === 'student-view' || assessmentPhase === 'student-view' ||
              (assessmentPhase === 'overview' && role === 'student')) && (
              <button className="am-close" onClick={onClose}>✕</button>
            )}

          </div>

        </div>

        {/* BODY */}
        <div className="am-body" ref={modalBodyRef}>
          {assessmentPhase === 'grading' ? (
            renderGradingPhase()
          ) : assessmentPhase === 'student-view' ? (
            renderStudentViewPhase()
          ) : assessmentPhase === 'attempt' ? (
            renderAttemptPhase()
          ) : assessmentPhase === 'results' ? (
            renderResultsPhase()
          ) : role === 'student' && assessmentPhase === 'overview' ? (
            renderStudentOverview()
          ): editing ? (
            <>
              {assessmentType === 'lab'  && renderLabEdit(editedContent?.[0] || content)}
              {assessmentType === 'quiz' && renderQuizExamEdit(editedContent?.[0] || content)}
              {assessmentType === 'exam' && renderQuizExamEdit(editedContent?.[0] || content)}
            </>
          ) : (
            <>
              {assessmentType === 'lab'  && renderLabView(content)}
              {assessmentType === 'quiz' && renderQuizExamView(content)}
              {assessmentType === 'exam' && renderQuizExamView(content)}
            </>
          )}
        </div>

        {/* FOOTER */}
        {/* <div className="am-footer">
          <div className="am-footer-space" />
          {phase === 'grading' ? (
            <>
              <button className="am-btn am-btn--ghost" onClick={onClose}>Close</button>
              {(() => {
                const hasSubmitted  = submission?.status === 'submitted' || submission?.status === 'graded';
                const hasAnswers    = submission?.answers?.length > 0;
                const hasLabWork    = submission?.labSessions && Object.keys(submission.labSessions).length > 0;
                const hasCircuits   = allStudentCircuits?.some(c =>
                  c.courseId === courseId &&
                  c.labId === submission?.labId &&
                  (c.studentId === submission?.loginId || c.studentId === submission?.studentLoginId)
                );
                const isInProgress  = !hasSubmitted && (hasAnswers || hasLabWork || hasCircuits);
                const notStarted    = !hasSubmitted && !hasAnswers && !hasLabWork && !hasCircuits;

                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {hasSubmitted && <span style={{ fontSize: '12px', color: 'var(--m-green)', fontWeight: 600 }}>✅ Submitted</span>}
                    {isInProgress && <span style={{ fontSize: '12px', color: 'var(--m-amber)', fontWeight: 600 }}>📝 In Progress</span>}
                    {notStarted   && <span style={{ fontSize: '12px', color: 'var(--m-text-muted)', fontWeight: 600 }}>🔒 Not Started</span>}

                    
                    {(hasSubmitted || isInProgress) && (
                      <button
                        className="am-btn am-btn--secondary"
                        onClick={() => setAssessmentPhase('student-view')}
                      >
                        👁️ View Student Work
                      </button>
                    )}

                    
                    {hasSubmitted ? (
                      <button
                        className="am-btn am-btn--secondary"
                        onClick={() => onSubmitGrade?.(submission, gradeFormData)}
                      >
                        ✏️ Override Grade
                      </button>
                    ) : (
                      <button
                        className="am-btn am-btn--primary"
                        onClick={() => onSubmitGrade?.(submission, gradeFormData)}
                      >
                        💾 Submit Grade
                      </button>
                    )}
                  </div>
                );
              })()}
            </>
          ) : phase === 'student-view' || assessmentPhase === 'student-view' ? (
            <>
              {assessmentPhase === 'student-view' && phase === 'grading' && (
                <button className="am-btn am-btn--ghost" onClick={() => setAssessmentPhase('grading')}>← Back to Grading</button>
              )}
              <button className="am-btn am-btn--ghost" onClick={onClose}>Close</button>
            </>
          ) : assessmentPhase === 'attempt' ? (
            <>
              <button className="am-btn am-btn--ghost" onClick={onClose}>Cancel</button>
              <button
                className="am-btn am-btn--primary"
                disabled={isSubmitting || uploadingFiles}
                onClick={handleSubmitAttempt}
              >
                {uploadingFiles ? '⏫ Uploading...' : isSubmitting ? '⏳ Submitting...' : `📤 Submit ${typeCfg.label}`}
              </button>
            </>
          ) : assessmentPhase === 'results' ? (
             <>
              <button 
                className="am-btn am-btn--primary" 
                disabled={isGrading}
                onClick={() => {
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                  }
                  onClose();
                }}
              >
                {isGrading ? '⏳ Grading...' : '✓ Done'}
              </button>
            </>
          ) : role === 'student' && phase === 'overview' ? (
            <>
              <button className="am-btn am-btn--ghost" onClick={onClose}>Close</button>
              {(() => {
                const attemptsUsed    = (() => {
                  const idField = typeCfg.idField;
                  const itemId  = content?.[idField];
                  // if (assessmentType === 'lab')  return courseProgress?.labProgress?.labs?.find(l => l.labId === itemId)?.attempts?.length || 0;
                  // if (assessmentType === 'quiz') return courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId)?.attempts?.length || 0;
                  // if (assessmentType === 'exam') return courseProgress?.examProgress?.exams?.find(e => e.examId === itemId)?.attempts?.length || 0;
                  if (assessmentType === 'lab')  return courseProgress?.labProgress?.labs?.find(l => l.labId === itemId)?.attemptsUsed || 0;
                  if (assessmentType === 'quiz') return courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId)?.attemptsUsed || 0;
                  if (assessmentType === 'exam') return courseProgress?.examProgress?.exams?.find(e => e.examId === itemId)?.attemptsUsed || 0;
                  return 0;
                })();
                const attemptsAllowed = content?.attemptsAllowed || 1;
                const exhausted       = attemptsUsed >= attemptsAllowed;
                if (exhausted) return (
                  <span style={{ fontSize: '13px', color: 'var(--m-red)' }}>
                    ⚠️ All attempts used ({attemptsUsed}/{attemptsAllowed})
                  </span>
                );
                const attemptNum = attemptsUsed + 1;
                const attemptLabel = attemptNum === 1 ? '' :
                                     attemptNum === 2 ? ' (2nd Attempt)' :
                                     attemptNum === 3 ? ' (3rd Attempt)' :
                                     ` (${attemptNum}th Attempt)`;
                return (
                  <button
                    className="am-btn am-btn--primary"
                    onClick={() => {
                      setAnswers({});
                      setEssayFiles({});
                      setSubmissionResults(null);
                      setAssessmentPhase('attempt');
                    }}
                  >
                    ▶️ Start {typeCfg.label}{attemptLabel}
                  </button>
                );
              })()}
            </>
          ): editing ? (
            <>
              <button className="am-btn am-btn--ghost"   onClick={handleCancelEdit}>Cancel</button>
              <button className="am-btn am-btn--primary" onClick={handleSaveEdit}>💾 Save Changes</button>
            </>
          ) : (
            <button className="am-btn am-btn--ghost" onClick={onClose}>Close</button>
          )}
        </div> */}

      </div>

      <CustomAlert
        visible={submitConfirmAlert}
        title="Unanswered Questions"
        icon="⚠️"
        message={submitConfirmMessage}
        onConfirm={() => {
          setSubmitConfirmAlert(false);
          handleConfirmedSubmitAttempt();
        }}
        onCancel={() => setSubmitConfirmAlert(false)}
      />

      <CustomAlert
        visible={validationAlert}
        title="Validation Error"
        icon="⚠️"
        message={validationAlertMessage}
        onClose={() => setValidationAlert(false)}
      />

    </div>
  );
};

export default AssessmentModal;
